/**
 * ClipEditorModal
 *
 * Full-screen modal for recording a voiceover clip over 1–2 video sources.
 * The modal captures a composite screengrab (video panels + annotation drawings)
 * combined with microphone audio into a single .webm blob.
 *
 * Each video panel has its own independent annotation toolbar (brush / eraser / colors),
 * enabled only while that panel's video is paused.
 */

import { FitText } from './FitText';
import React, { useRef, useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import type { NotableSegment, VoiceoverNotableSegmentClip } from '../types/videoEdit';
import type { DrawColor } from './VideoCanvasOverlay';
import {
  EBTN_PRIMARY,
  EBTN_SECONDARY,
  EBTN_GHOST,
  EBTN_ICON,
  EBTN_TRANSPORT,
  EBTN_DESTRUCTIVE,
  EBTN_RECORD,
} from './videoEditor/editorButtonStyles';
import { usePhoneLayout } from './videoEditor/usePhoneLayout';
import { PHONE_HEADER, stackedPanelBounds, fitAspect, DEFAULT_ASPECT } from './videoEditor/phoneLayout';
import { EditorDebugOverlay } from './videoEditor/EditorDebugOverlay';
import { PHONE_INLINE_EXTRAS } from './videoEditor/phoneInlineVideo';
import { auth } from '../firebase';
import { usePinchZoom, IDENTITY_ZOOM, type ZoomState } from './videoEditor/usePinchZoom';
import { ZoomResetChip } from './videoEditor/ZoomResetChip';
import { secondFingerAction, strokeShows, isTap, strokeArmed, TAP_SLOP } from './videoEditor/drawGesture';
import { nextMarker } from './videoEditor/markerCycle';
import { takeOutcome, type TakeEnd } from './videoEditor/takeOutcome';
import { recorderTap, STOP_WATCHDOG_MS } from './videoEditor/recorderTaps';
import { syncPairMutes, type PairMuteRefs } from './videoEditor/pairMutes';
import { BUBBLE_HOME, clampBubble, formatBubble, parseBubble, type BubbleInsets, type BubblePos } from './videoEditor/bubbleGeometry';
import { nextPlaybackUi, type MediaSignal, type PlaybackUi } from './videoEditor/playbackState';
import { debugNote } from './videoEditor/debugNotes';
import { acquireCapture, currentCapture, onCaptureChange } from './videoEditor/captureSession';
import { segLength, toRel, toAbs, readout, outOfRange, parkAt } from './videoEditor/segmentPlayback';
import { DrawToolbar, PanelUtilities } from './videoEditor/editorControls';
import {
  applyVideoState, COLOR_HEX, DRAW_COLORS, SPEEDS, type EditorTool as DrawingTool, type Speed,
} from './videoEditor/editorControlValues';
import { RecordSaveButton, TakeClock, TrashButton, SavedToast, RecorderAlert, CameraToggle, RecButton, SpeedButton, CloseX, MuteGlyph, ColumnChevron } from './videoEditor/RecorderControls';
import { landscapeSingleLayout, landscapePairLayout, portraitBounds, pairRowMinW, readoutChars, rowGap, REC, RECORD_ZONE_H } from './videoEditor/recorderLayout';
import BackButton from './BackButton';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ClipSource {
  videoUrl: string;
  segment?: NotableSegment; // if set, playback is constrained to segment bounds
  label?: string;
  /** When true, uses the user's camera as the video source (no video file) */
  isCameraSource?: boolean;
  /** Visual rotation applied to this source's video (0 | 90 | 180 | 270). Default: 0. */
  rotation?: number;
}

export interface ClipEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when user clicks "Save & Close" */
  onSave: (clip: VoiceoverNotableSegmentClip) => void;
  /**
   * Called when user clicks "Save & Continue" — modal stays open for another
   * recording. On a phone it may return a promise that settles once the take
   * is kept on the device; "Clip saved" waits for it (spec 13.7).
   */
  onSaveAndContinue: (clip: VoiceoverNotableSegmentClip) => void | Promise<void>;
  primarySource: ClipSource;
  secondarySource?: ClipSource;
  /**
   * When true, renders inline (filling its parent container) instead of as a
   * fixed full-screen overlay. Used inside LessonVideoEditor so the clip editor
   * sits alongside the Lesson Organizer panel.
   */
  embedded?: boolean;
  /** Dev preview: start in recording mode (shows Save & Continue / Save & Close / Abort) */
  initiallyRecording?: boolean;
  /**
   * When provided and no secondarySource is set, shows an "Add video source"
   * button in the header so the user can add a side-by-side comparison source
   * without going back through the picker flow.
   */
  onAddSecondarySource?: () => void;
}


// ── Constants ──────────────────────────────────────────────────────────────────

/** Composite canvas panel dimensions (16:9) — 720p per panel */
const PANEL_W = 1280;
const PANEL_H = 720;
/**
 * Longest edge of a single-source composite. The canvas is sized to the
 * source's own aspect rather than to a fixed landscape frame, so a portrait
 * video fills it instead of being object-contained inside 1280x720 and having
 * 68.4% of every frame painted black. Nothing re-encodes downstream, so those
 * bars were permanent and the player's object-contain added a second set.
 *
 * Capping the long edge keeps the pixel count in the same range: 16:9 still
 * lands on exactly 1280x720, and the worst case (4:3 or 3:4) is 1280x960,
 * a third more pixels rather than a different order of magnitude.
 */
const PANEL_LONG_EDGE = 1280;

/**
 * Keeps the whole bubble inside its box, not just its centre.
 *
 * The bubble's diameter is 10% of the box width, so its radius is 0.05 in
 * normalized x. In y the same radius is a different fraction, because the box
 * is not square, which is why the two axes clamp differently. The old 0.05 to
 * 0.95 on both axes clamped the centre and let half the bubble hang out.
 */
/**
 * Bubble diameter as a fraction of the box width. The single source of truth:
 * the on-screen element, the recorded radius and the edge clamp all derive
 * from it, so the framed bubble and the recorded one cannot drift apart.
 *
 * 0.15 rather than 0.10 because of the surface these are watched on. A 16:9
 * clip on a 390pt phone renders about 58pt across at 0.15; at 0.10 it was
 * about 39pt, too small to read a face.
 */
/**
 * Diameter as a fraction of one panel — not of the pair.
 *
 * It used to be a fraction of whatever box the bubble floated over, which in
 * side by side is both panels, so the paired bubble rendered at twice the
 * single-source size. Dividing by the panel makes the composite draw a 192px
 * bubble in both layouts — identical in the delivered file — and returns single
 * source to exactly what it was.
 *
 * The paired preview is smaller in absolute pixels because a paired panel is
 * displayed at half width. That is accurate to what records, not too small.
 */
const PIP_DIAMETER_FRACTION = 0.15;
/**
 * Single source gets a bigger bubble than a paired panel.
 *
 * In side by side the bubble floats over one of two videos and is competing
 * for the coach's attention with the other; at 0.15 it is the right weight.
 * Single source has no competition and the same fraction reads as an
 * afterthought, so it gets 0.22.
 */
const PIP_DIAMETER_FRACTION_SINGLE = 0.22;
/**
 * Phone: about 28% of the stage's width, in every mode, so a face reads on a
 * 428-wide screen. The composite uses the same rule on a phone, so what
 * records matches what the coach framed.
 */
const PIP_DIAMETER_FRACTION_PHONE = 0.28;

/**
 * The bubble's diameter, from a panel's own dimensions.
 *
 * Keyed off the panel's LONGER edge, not its width. The width was fine for
 * landscape and quietly wrong for everything else: a 9:16 clip composites to a
 * 720x1280 frame, so 15% of the width drew a 108px bubble where a 16:9 clip of
 * the same lesson drew 192px. Same constant, same coach, nearly half the size,
 * for no reason other than the source being portrait. `compositeSizeFor`
 * normalises the long edge to PANEL_LONG_EDGE, so keying off the long edge
 * makes the recorded bubble one size in every aspect.
 *
 * Both the on-screen bubble and the composite call this with their own panel
 * dimensions, which is what keeps the preview honest about what records.
 */
function pipDiameterFor(panelW: number, panelH: number, paired: boolean, phone = false): number {
  if (phone) return panelW * PIP_DIAMETER_FRACTION_PHONE;
  const fraction = paired ? PIP_DIAMETER_FRACTION : PIP_DIAMETER_FRACTION_SINGLE;
  return Math.max(panelW, panelH) * fraction;
}
/* ── Phone camera bubble (spec 10.7) ─────────────────────────────────────
   The bubble sits in a corner of one panel's picture: one panel for a
   single video, either of two side by side (panel 1 is the composite's left
   half, panel 2 its right, so the recording matches the screen in both
   orientations). 18% of the stage width clamped 64 to 112 for one video,
   22% of one panel's width clamped 56 to 88 for two. Corners sit 8 in from
   every edge (spec 15.11). The composite draws the same size and insets
   relative to its panel. */
/* Spec 15.14: whether the landscape columns are collapsed, kept for the
   session (this page load, and the tab's session storage across reloads). */
function readColumnsCollapsed(): boolean {
  try { return sessionStorage.getItem('lve.recorderColumnsCollapsed') === '1'; } catch { return false; }
}
function rememberColumnsCollapsed(v: boolean) {
  try { sessionStorage.setItem('lve.recorderColumnsCollapsed', v ? '1' : '0'); } catch { /* not remembered across reloads */ }
}
const PHONE_PIP_INSET = 8;
/* Spec 15.11: the bubble reaches every corner of its video, 8 in from each
   edge, in every layout. (A 40 top inset, kept for a title and a time chip
   that are no longer drawn on the video, stopped it about a quarter of the
   way down.) */
const PHONE_BUBBLE_INSETS: BubbleInsets = { top: PHONE_PIP_INSET, right: PHONE_PIP_INSET, bottom: PHONE_PIP_INSET, left: PHONE_PIP_INSET };
const phonePipDiameter = (panelWidth: number, paired: boolean) => Math.round(paired
  ? Math.min(88, Math.max(56, 0.22 * panelWidth))
  : Math.min(112, Math.max(64, 0.18 * panelWidth)));
const pipCornerKey = () => `lve.pipCorner.${auth.currentUser?.uid ?? 'anon'}`;
/** The remembered place (spec 13.10). An older corner-only value reads as that corner. */
const pipPosKey = () => `lve.pipPos.${auth.currentUser?.uid ?? 'anon'}`;
function readBubble(): BubblePos {
  try { return parseBubble(localStorage.getItem(pipPosKey()) ?? localStorage.getItem(pipCornerKey())); } catch { return BUBBLE_HOME; }
}
function saveBubble(p: BubblePos) {
  try { localStorage.setItem(pipPosKey(), formatBubble(p)); } catch { /* storage blocked: not remembered */ }
}
/* The camera's on/off, remembered per coach on a phone (spec 10.7). */
const pipOnKey = () => `lve.pipOn.${auth.currentUser?.uid ?? 'anon'}`;
function readPipOn(): boolean {
  try { return localStorage.getItem(pipOnKey()) !== 'off'; } catch { return true; }
}
function savePipOn(on: boolean) {
  try { localStorage.setItem(pipOnKey(), on ? 'on' : 'off'); } catch { /* storage blocked: not remembered */ }
}

/** Default inset from the bottom and right edges, and the clamp's margin. */
const PIP_EDGE_INSET_PX = 16;

/**
 * Keeps the bubble wholly inside the box, off its edges, and off the divider.
 *
 * Bounds are on the bubble's edges rather than its centre, so no part of it can
 * leave the picture. The seam is not out of bounds: the coach drags the bubble
 * and decides where it belongs, including between the two panels.
 *
 * Both the preview and the composite run positions through this, which is what
 * keeps them agreeing wherever the coach drags to.
 */
function clampPip(
  x: number,
  y: number,
  boxW: number,
  boxH: number,
  diameter: number,
): { x: number; y: number } {
  const rx = boxW > 0 ? (diameter / 2 + PIP_EDGE_INSET_PX) / boxW : 0.5;
  const ry = boxH > 0 ? (diameter / 2 + PIP_EDGE_INSET_PX) / boxH : 0.5;
  const cx = rx * 2 >= 1 ? 0.5 : Math.max(rx, Math.min(1 - rx, x));
  const cy = ry * 2 >= 1 ? 0.5 : Math.max(ry, Math.min(1 - ry, y));
  return { x: cx, y: cy };
}

/** A measured box, relative to some container. */
type DOMRectLike = { left: number; top: number; width: number; height: number };

/** VP9 requires even dimensions. */
const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);

/**
 * Composite size for one source. Falls back to the old fixed frame when the
 * video has not reported its dimensions yet.
 */
function compositeSizeFor(vw: number, vh: number): { w: number; h: number } {
  if (!vw || !vh) return { w: PANEL_W, h: PANEL_H };
  const scale = PANEL_LONG_EDGE / Math.max(vw, vh);
  return { w: even(vw * scale), h: even(vh * scale) };
}

/**
 * Tolerance used in the startTime boundary check inside handleTimeUpdate.
 * After video.play() is called with currentTime exactly at segStart, the browser
 * fires timeupdate almost immediately.  Floating-point representation means
 * video.currentTime can be a tiny fraction below seg.startTime even though they
 * are "the same" position.  Without this epsilon the boundary check fires
 * `video.currentTime = seg.startTime`, which aborts the pending play() promise
 * (per the HTML spec, setting currentTime while play is pending causes AbortError).
 * 50 ms is imperceptible and well within the accuracy of the range input (step 0.05 s).
 */
const SEGMENT_START_EPSILON = 0.05;
/**
 * Phone: how close to a segment's end counts as the end when play is
 * pressed. A panel that reached its end is parked at seg.endTime, and iOS
 * reads currentTime back a hair below what was set, so without this margin
 * play started at the end, met it at once and stopped: play did nothing.
 */
const SEGMENT_END_EPSILON_PHONE = 0.05;

// Brush/eraser sizes are in canvas coordinates.
// At 1280×720 canvas displayed ~640px wide on-screen, the scale factor is ~2×,
// so these values are doubled from the old 640×360 defaults to maintain the same
// visual stroke width when the user draws.
const BRUSH_SIZE = 10;
const ERASER_SIZE = 40;

/** The phone marker's tap order (spec 13.1): the palette as listed. */
const MARKER_PALETTE: readonly DrawColor[] = DRAW_COLORS.map((c) => c.value);

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/** Shows tenths of a second — used for the in-panel timestamp watermark */
function formatTimeDetailed(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const whole = Math.floor(sec).toString().padStart(2, '0');
  const frac = Math.floor((sec % 1) * 10);
  return `${m}:${whole}.${frac}`;
}

type StoredAnnotation = { timestamp: number; imageData: ImageData };
/** One undo step: the annotation at `timestamp` before a stroke, or null if there was none. */
type HistoryEntry = { timestamp: number; imageData: ImageData | null };
const MAX_UNDO_STEPS = 10;
const ANNOTATION_DURATION = 1.0; // seconds — how long each annotation is visible on the timeline

/**
 * Return the annotation whose 1s window covers `time`, preferring the most
 * recent one when multiple annotations overlap (timestamps within 1s of each other).
 */
function findActiveAnnotation(
  annotations: StoredAnnotation[],
  time: number
): StoredAnnotation | null {
  const inRange = annotations.filter(
    (a) => time >= a.timestamp && time < a.timestamp + ANNOTATION_DURATION
  );
  if (inRange.length === 0) return null;
  // Most recent timestamp wins if multiple are in range
  return inRange.reduce((best, a) => (a.timestamp > best.timestamp ? a : best));
}

/**
 * Apply speed and mute state to a video element in one atomic operation.
 * Always mutes at 0.5× and below (pitched-down audio is unintelligible and
 * browsers cannot play it cleanly), and also respects the user's explicit
 * per-panel mute preference.
 * Keeping this as a single call (instead of separate muted/playbackRate
 * assignments spread across event handlers) eliminates the race condition where
 * handleSeeking muted the video and handleSeeked tried to restore state at an
 * unpredictable time relative to the play() call.
 */


// ── VideoPanel sub-component ───────────────────────────────────────────────────


const VideoPanel = forwardRef<VideoPanelHandle, VideoPanelProps>(
  (props, ref) => VideoPanelInner(props, ref),
);
VideoPanel.displayName = 'VideoPanel';


/**
 * The end of a panel's playable range.
 *
 * There used to be two answers to this in one file. The transport's scrubber
 * used `segment?.endTime ?? duration`; the play handler used
 * `segment?.endTime ?? Infinity`. Both were locally defensible and they meant
 * different things, which is the kind of divergence that is fine right up
 * until someone changes one of them.
 *
 * One meaning now: the segment's end, or failing that the end of the video.
 * `duration` is the honest answer for both — a source with no segment is
 * scrubbable to its end, and reaching that end is reaching the end.
 *
 * The case that needs care is duration not being known yet. Before metadata
 * loads it is 0 or NaN, and returning that would make `currentTime >= segEnd`
 * true at rest, so every play click would re-seek to the start. Infinity is
 * the safe reading of "no end known yet": never treat the clip as finished.
 */
function segmentEndOf(segEnd: number | null, duration: number): number {
  if (segEnd !== null) return segEnd;
  return Number.isFinite(duration) && duration > 0 ? duration : Infinity;
}

/**
 * One panel's playback controls: play, scrubber, timecode, speed, mute.
 *
 * A component rather than JSX inside VideoPanel because side by side renders
 * both of these in the parent, below the shared drawing tools — the tools
 * belong nearer the video than the transport does. Single source still renders
 * it inside its own panel, unchanged.
 */
function PanelTransport({
  showVideoControls, sideBySide, isPlaying, onTogglePlay,
  segStart, segEnd, currentTime, onSeek, onScrubStart, onScrubEnd,
  speed, onSpeedChange, isMuted, onToggleMute, phone = false, utilities, narrow = false, compact = false, loading = false,
}: {
  /** Phone (spec 13.8): play was asked for and the video cannot play yet: a spinner. */
  loading?: boolean;
  /** Phone landscape (spec 10.5): the thin 32 timeline row under the video. */
  compact?: boolean;
  /**
   * Phone landscape: speed and mute at the end of the same row (56 tall), or,
   * where `narrow`, on a second row of their own so nothing overlaps.
   */
  utilities?: React.ReactNode;
  narrow?: boolean;
  /**
   * Phone: one 44 row of play, scrubber and times. Every child is flex: none
   * except the scrubber, so nothing can be laid over it at 375, and the source
   * time stacks inside the times' own box instead of overhanging the row
   * below. Speed and mute live elsewhere on a phone.
   */
  phone?: boolean;
  showVideoControls: boolean;
  sideBySide: boolean;
  isPlaying: boolean;
  onTogglePlay: () => void;
  segStart: number;
  segEnd: number;
  currentTime: number;
  onSeek: (t: number) => void;
  onScrubStart: () => void;
  onScrubEnd: () => void;
  speed: Speed;
  onSpeedChange: (s: Speed) => void;
  isMuted: boolean;
  onToggleMute: () => void;
}) {
  if (phone) {
    const range = { start: segStart, end: segEnd };
    /* Spec 15: the landscape pill is 40, the portrait row 44. */
    const rowH = utilities ? (narrow ? 'h-11' : 'h-14') : compact ? 'h-10' : 'h-11';
    if (!showVideoControls) {
      return utilities
        ? <div className={`flex items-center justify-end flex-none ${narrow ? 'h-[96px]' : 'h-14'}`}>{utilities}</div>
        : <div className={`${compact ? 'h-8' : 'h-11'} flex-none`} aria-hidden="true" />;
    }
    const row = (
      /* Landscape (compact, spec 13.15): the row fills the timeline pill it sits in. */
      <div className={`flex items-center gap-2 ${rowH} ${compact ? 'flex-1' : 'flex-none'} min-w-0`}>
        <button
          type="button"
          onClick={onTogglePlay}
          className={`${EBTN_TRANSPORT} flex-none`}
          title={isPlaying ? 'Pause' : 'Play'}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          aria-busy={loading || undefined}
        >
          {loading ? (
            <span className="w-[18px] h-[18px] rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden="true" />
          ) : isPlaying ? (
            <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        {/* Spec 11.1: the timeline runs 0 to the segment's length, and the
            readout comes from the same value, so the two always agree. */}
        <input
          type="range"
          min={0}
          max={segLength(range)}
          step={0.05}
          value={toRel(currentTime, range)}
          onChange={(e) => onSeek(toAbs(parseFloat(e.target.value), range))}
          onPointerDown={onScrubStart}
          onPointerUp={onScrubEnd}
          onPointerCancel={onScrubEnd}
          onKeyDown={onScrubStart}
          onKeyUp={onScrubEnd}
          onBlur={onScrubEnd}
          aria-label="Scrub"
          /* Spec 15: the scrubber takes every pixel the play button and the
             time leave, and never less than half the row. */
          className={`flex-1 min-w-[50%] ${compact ? 'h-10' : 'h-11'} lve-phone-range`}
        />
        {/* Spec 15: times are mono at Subhead 15/20. No source timecode
            under it (spec 9.3). */}
        <span className="flex-none w-max font-mono text-[15px] leading-[20px] text-[var(--editor-text)] tabular-nums pr-2" data-testid="segment-readout">
          {readout(toRel(currentTime, range), segLength(range))}
        </span>
        {utilities && !narrow && <div className="flex-none">{utilities}</div>}
      </div>
    );
    if (utilities && narrow) {
      return (
        <div className="flex flex-col flex-none h-[96px] justify-center">
          {row}
          <div className="flex items-center justify-end h-11">{utilities}</div>
        </div>
      );
    }
    return row;
  }
  return (
    <>
      {!showVideoControls && sideBySide && <div className="h-9 flex-shrink-0" aria-hidden="true" />}

      {/* Speed + Playback controls — hidden for blank page and freeze frame */}
      {showVideoControls && (
        <div className={`flex items-center flex-shrink-0 ${sideBySide ? 'gap-3 h-9 phone:h-auto phone:flex-wrap phone:gap-y-1' : 'gap-2'}`}>
          <button
            type="button"
            onClick={onTogglePlay}
            /* No inline background: that was why this button had no hover. An
               inline style beats every stylesheet rule short of !important, so
               .ebtn-icon:hover could never repaint it. The resting fill now
               lives on the pseudo-element that also carries the hover. */
            className={EBTN_TRANSPORT}
            title={isPlaying ? 'Pause' : 'Play'}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <input
            type="range"
            min={segStart}
            max={segEnd}
            step={0.05}
            value={Math.min(Math.max(currentTime, segStart), segEnd)}
            onChange={(e) => onSeek(parseFloat(e.target.value))}
            /* Both pointer and keyboard. A range input is focusable and arrow
               keys move it without ever firing a pointer event, so pointer
               handlers alone would leave keyboard scrubbing fighting playback
               exactly as the drag used to. onKeyUp/onBlur close it. */
            onPointerDown={onScrubStart}
            onPointerUp={onScrubEnd}
            onPointerCancel={onScrubEnd}
            onKeyDown={onScrubStart}
            onKeyUp={onScrubEnd}
            onBlur={onScrubEnd}
            /* 44 tall on a phone: the track still draws thin, the box around
               it is what a finger has to land in. */
            className="flex-1 h-1 phone:h-11 accent-db-accent"
          />
          {/* "in segment", because segEnd is the segment's end and a coach who
              has been watching a 9:11 source otherwise reads this as the video
              having changed length. */}
          {/* The numbers are the flex child, so `items-center` centres them on
              the row and they line up with the scrubber. The qualifier is taken
              out of flow beneath them, which is the only way it can sit below
              without pushing them up. It overhangs the row by about 6, into the
              panel container's own 16 of bottom padding, so nothing clips it
              and nothing moves. */}
          {/* The scrubber is segment-scoped, so the segment-relative time is
              what it points at and leads here. The absolute position sits under
              it, quieter, for anyone matching against the source video. No
              label: two numbers in two weights say which is which. No segment
              boundary is computed here — segStart and segEnd are the same values
              the scrubber already uses. */}
          <span className="relative w-max flex-shrink-0 text-xs leading-[14px] font-mono text-[var(--editor-text)]">
            {formatTime(Math.max(0, currentTime - segStart))} / {formatTime(Math.max(0, segEnd - segStart))}
            <span className="absolute right-0 top-full text-[10px] leading-3 font-normal text-[var(--editor-text-muted)]">
              {formatTime(currentTime)}
            </span>
          </span>
          {sideBySide && <PanelUtilities showVideoControls={showVideoControls} speed={speed} onSpeedChange={onSpeedChange} isMuted={isMuted} onToggleMute={onToggleMute} />}
        </div>
      )}
    </>
  );
}

interface VideoPanelProps {
  /** Phone (spec 13.8): a spinner in the play button while the video cannot play yet. */
  isLoading?: boolean;
  source: ClipSource;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Told when this panel's <video> element is attached or replaced (spec 13.9). */
  onVideoElement?: (el: HTMLVideoElement | null) => void;
  /** Told when this panel's strokes and their history are cleared (spec 13.11). */
  onHistoryReset?: () => void;
  annotationCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  speed: Speed;
  isMuted: boolean;
  onTogglePlay: () => void;
  onSeek: (t: number) => void;
  onScrubStart: () => void;
  onScrubEnd: () => void;
  onSpeedChange: (s: Speed) => void;
  onToggleMute: () => void;
  /** Optional overlay element rendered inside the video area (e.g. PiP camera circle) */
  pipOverlay?: React.ReactNode;
  /**
   * True when this panel is one of a pair.
   *
   * Playback is per-panel — each video has its own play, seek, speed and mute,
   * which is the point of comparing two clips — so a single shared transport
   * is not available. Instead the chrome a panel does not need is reserved as
   * empty space, so both videos get the same box. Off in the single-source
   * case, where reserving anything would only cost video height.
   */
  sideBySide?: boolean;
  /** Reports the drawn picture's height, so the parent can match the divider to it. */
  onVideoBoxHeight?: (h: number) => void;
  /**
   * The tool and colour, owned by the parent so both panels share one control.
   * Which canvas a stroke lands on is still decided by the pointer, not by
   * this — nothing about when or where a stroke commits changes.
   */
  drawingTool: DrawingTool;
  drawColor: DrawColor;
  onSetDrawingTool: React.Dispatch<React.SetStateAction<DrawingTool>>;
  onSetDrawColor: (c: DrawColor) => void;
  /** Called after a stroke is committed, so the parent can record the order. */
  onStrokeCommitted?: () => void;
  /**
   * Phone: the box this panel's stage must fit in, from the viewport when the
   * recorder opened (phoneLayout.ts). The stage is this panel's own video's
   * aspect, the largest that fits: 16:9 until the video reports its size,
   * then the real shape once, animated, and then held.
   */
  phoneBounds?: { maxW: number; maxH: number };
  /**
   * Phone, side by side: the panel carries its own label row (name, speed,
   * mute) above the stage and its transport below. Single source puts its
   * transport and tools in the recorder's bottom dock instead.
   */
  phoneRows?: boolean;
  /** Phone: this panel's video's aspect once known, for the stacked pair's split. */
  onPhoneAspect?: (aspect: number) => void;
  /** Phone: where this panel's pinch zoom is published, for the composite (spec 9.7). */
  zoomStateRef?: React.MutableRefObject<ZoomState>;
  /** Phone: which panel this is, marked on its picture for the bubble (spec 10.7). */
  phoneStageId?: 1 | 2;
  /** Phone, side by side: how far the title starts in, to clear the close X (15.7). */
  phoneTitleIndent?: number;
  /** Phone, side by side in portrait: the row's padding inside a full-width video (15.4). */
  phoneRowInset?: number;
  /**
   * Phone (spec 15.16): a single tap on the video, one finger, under 250ms
   * and under 8 of movement. It never draws: a stroke needs more than 8.
   */
  onPhoneTap?: () => void;
  /**
   * Phone, side by side (spec 15.17): the scrubber drives the element
   * directly while a finger is on it, with no state round trip.
   */
  onPhoneScrub?: { begin: () => void; move: (t: number) => void; end: (t: number) => void };
  /** Phone, side by side (spec 15.18): both videos play, so both are muted and mute is locked. */
  phoneMuteLocked?: boolean;
}

/** What the parent can drive from the shared Undo and Redo. */
export interface VideoPanelHandle {
  /** Returns false when there was nothing left to step back to. */
  undo: () => boolean;
  redo: () => boolean;
}

function VideoPanelInner({
  source,
  videoRef,
  annotationCanvasRef,
  isPlaying,
  currentTime,
  duration,
  speed,
  isMuted,
  onTogglePlay,
  onSeek,
  onScrubStart,
  onScrubEnd,
  onSpeedChange,
  onToggleMute,
  pipOverlay,
  sideBySide = false,
  onVideoBoxHeight,
  drawingTool,
  drawColor,
  onSetDrawingTool,
  onSetDrawColor,
  onStrokeCommitted,
  phoneBounds,
  phoneRows = false,
  onPhoneAspect,
  zoomStateRef,
  phoneStageId,
  onVideoElement,
  isLoading = false,
  onHistoryReset,
  phoneTitleIndent = 0,
  phoneRowInset = 0,
  onPhoneTap,
  onPhoneScrub,
  phoneMuteLocked = false,
}: VideoPanelProps, ref: React.Ref<VideoPanelHandle>) {
  /* The element goes into the parent's ref, and the parent hears when it
     changes, so nothing keeps listening to a replaced element (spec 13.9). */
  const onVideoElementRef = useRef(onVideoElement);
  useEffect(() => { onVideoElementRef.current = onVideoElement; });
  const setVideoEl = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    onVideoElementRef.current?.(el);
  }, [videoRef]);
  const panelPhone = usePhoneLayout();
  const isMobile = panelPhone.phone;
  /* This panel's video's shape, once known (a camera's too), for the phone stage. */
  const [phoneAspect, setPhoneAspect] = useState<number | null>(null);
  const [phoneSettling, setPhoneSettling] = useState(false);
  const onPhoneAspectRef = useRef(onPhoneAspect);
  useEffect(() => { onPhoneAspectRef.current = onPhoneAspect; });
  useEffect(() => {
    if (!phoneBounds) return;
    const video = videoRef.current;
    if (!video) return;
    const read = () => {
      if (!video.videoWidth || !video.videoHeight) return;
      const rot = source.rotation ?? 0;
      const a = rot === 90 || rot === 270 ? video.videoHeight / video.videoWidth : video.videoWidth / video.videoHeight;
      onPhoneAspectRef.current?.(a);
      setPhoneAspect((prev) => {
        if (prev === null) {
          setPhoneSettling(true);
          setTimeout(() => setPhoneSettling(false), 300);
        }
        return prev !== null && Math.abs(prev - a) < 0.001 ? prev : a;
      });
    };
    read();
    video.addEventListener('loadedmetadata', read);
    video.addEventListener('resize', read);
    return () => { video.removeEventListener('loadedmetadata', read); video.removeEventListener('resize', read); };
  // videoRef is a stable ref
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!phoneBounds, source.videoUrl, source.rotation, source.isCameraSource]);
  const phoneStage = phoneBounds ? fitAspect(phoneAspect ?? DEFAULT_ASPECT, phoneBounds.maxW, phoneBounds.maxH) : undefined;

  // Annotation canvas position — constrained to the video's CSS object-contain display area
  // so that canvas coords (0–1280, 0–720) map 1:1 to composite canvas coords. Without this,
  // strokes near the edges appear offset in the recording when the panel div's aspect ratio
  // differs from the video's (e.g., a 16:9 video in a 4:3 panel gets letterboxed differently
  // on screen vs. in the 1280×720 composite canvas).
  const panelAreaRef = useRef<HTMLDivElement>(null);
  /**
   * The area the box may occupy, and the box itself.
   *
   * Sized in JS rather than with `aspect-ratio`, because the CSS answer breaks
   * in exactly the case that matters here: with `aspect-ratio` plus both
   * `max-width` and `max-height`, whichever max clamps first makes that
   * dimension definite and the ratio is abandoned. Measuring the area and
   * computing `min(availW / srcW, availH / srcH)` is the same fit `object-fit:
   * contain` was already doing internally — so the video renders at exactly
   * the size it did before, and only the black around it goes away.
   */
  const videoAreaRef = useRef<HTMLDivElement>(null);
  const [videoBoxSize, setVideoBoxSize] = useState<{ w: number; h: number } | null>(null);
  const [annotationCanvasInset, setAnnotationCanvasInset] = useState<React.CSSProperties>({
    position: 'absolute', inset: 0, width: '100%', height: '100%',
  });
  const [panelAreaSize, setPanelAreaSize] = useState({ w: 0, h: 0 });

  const isCameraSource = source.isCameraSource === true;
  const isFreezeFrame = !isCameraSource && Boolean(source.segment) && source.segment!.startTime === source.segment!.endTime;

  /* Recomputes on area resize and on metadata, so growing the chrome below
     shrinks the box rather than reintroducing bars. */
  useEffect(() => {
    /* A phone stage is fixed and never fitted to the video: resizing the
       element after metadata is what the spec rules out (section 2), and one
       of the things WebKit stops repainting a playing video after. */
    if (phoneStage) return;
    if (!sideBySide) { setVideoBoxSize(null); return; }
    const area = videoAreaRef.current;
    const video = videoRef.current;
    if (!area) return;
    const fit = () => {
      const availW = area.clientWidth;
      const availH = area.clientHeight;
      if (!availW || !availH) return;
      const rot = source.rotation ?? 0;
      const swapped = rot === 90 || rot === 270;
      const vw0 = video?.videoWidth ?? 0;
      const vh0 = video?.videoHeight ?? 0;
      const srcW = swapped ? vh0 : vw0;
      const srcH = swapped ? vw0 : vh0;
      // No metadata yet: fill the area, which is what it did before.
      if (!srcW || !srcH) { setVideoBoxSize({ w: availW, h: availH }); onVideoBoxHeight?.(availH); return; }
      const scale = Math.min(availW / srcW, availH / srcH);
      const box = { w: Math.floor(srcW * scale), h: Math.floor(srcH * scale) };
      setVideoBoxSize(box);
      onVideoBoxHeight?.(box.h);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(area);
    video?.addEventListener('loadedmetadata', fit);
    return () => { ro.disconnect(); video?.removeEventListener('loadedmetadata', fit); };
  // videoRef is a stable ref
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideBySide, phoneStage, source.videoUrl, source.rotation, isCameraSource]);

  useEffect(() => {
    // Camera source fills the panel div with no letterboxing — no adjustment needed.
    if (isCameraSource) return;
    const video = videoRef.current;
    const panel = panelAreaRef.current;
    if (!video || !panel) return;

    const updateCanvasPosition = () => {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const panelW = panel.clientWidth;
      const panelH = panel.clientHeight;
      if (!panelW || !panelH) return;
      setPanelAreaSize({ w: panelW, h: panelH });
      if (!vw || !vh) return;
      // For 90/270° rotated sources the visual aspect ratio is flipped — swap dims so
      // the overlay canvas covers the actual letterboxed video area, not the full panel.
      const rotation = source.rotation ?? 0;
      const isSwapped = rotation === 90 || rotation === 270;
      const effectiveVW = isSwapped ? vh : vw;
      const effectiveVH = isSwapped ? vw : vh;
      const scale = Math.min(panelW / effectiveVW, panelH / effectiveVH);
      const vidW = effectiveVW * scale;
      const vidH = effectiveVH * scale;
      setAnnotationCanvasInset({
        position: 'absolute',
        left: `${(panelW - vidW) / 2}px`,
        top: `${(panelH - vidH) / 2}px`,
        width: `${vidW}px`,
        height: `${vidH}px`,
      });
    };

    video.addEventListener('loadedmetadata', updateCanvasPosition);
    if (video.readyState >= 1) updateCanvasPosition();
    const ro = new ResizeObserver(updateCanvasPosition);
    ro.observe(panel);
    return () => {
      video.removeEventListener('loadedmetadata', updateCanvasPosition);
      ro.disconnect();
    };
  // videoRef and panelAreaRef are stable refs; re-run when the video source or camera flag changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.videoUrl, isCameraSource, source.rotation]);

  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  /* Phone (spec 13.11): whether the stroke in progress shows anything. A
     tap that never moves, a stroke wholly outside the picture, and the
     eraser over nothing change nothing on screen and are not kept. */
  const strokeStartRef = useRef<{ x: number; y: number } | null>(null);
  const strokeMovedRef = useRef(false);
  const strokeInsideRef = useRef(false);

  // All annotations for this panel, each tied to a video timeline position.
  // An annotation is visible while currentTime ∈ [timestamp, timestamp + ANNOTATION_DURATION).
  const storedAnnotationsRef = useRef<StoredAnnotation[]>([]);
  const annotationTimestampRef = useRef<number>(0);

  /**
   * Undo and redo.
   *
   * There is no stroke list to walk back through: `handlePointerUp` snapshots
   * the whole canvas as ImageData and the snapshot replaces whatever was at
   * that timestamp. So the history is snapshot-based too. Each entry is the
   * annotation as it stood *before* one stroke, which `handlePointerDown`
   * already has to hand.
   *
   * Capped because a snapshot is PANEL_W x PANEL_H x 4 bytes: at 1280x720
   * that is 3.7MB per step, so ten steps is a ~37MB ceiling per panel. Raising
   * the cap is this one constant, at 3.7MB a step.
   */
  const undoStackRef = useRef<HistoryEntry[]>([]);
  const redoStackRef = useRef<HistoryEntry[]>([]);
  const [undoDepth, setUndoDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);
  /** The annotation as it stood before the stroke currently being drawn. */
  const strokeBaseRef = useRef<ImageData | null>(null);

  // Annotation tools are only available when this panel's video is paused
  /* Always. Drawing on a running video pauses it rather than being refused,
     so there is no state in which the pen is unavailable. */
  const canAnnotate = true;

  /* The tool used to be cleared when this panel started playing. It cannot be
     now — it is shared, and clearing it would disarm the pen on the paused
     panel because the other one is playing. The guard moved to the canvas:
     handlePointerDown refuses a stroke this panel cannot keep. */

  // Show or hide the active annotation based on the current video time
  useEffect(() => {
    if (isDrawingRef.current) return; // don't interfere while actively drawing
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const active = findActiveAnnotation(storedAnnotationsRef.current, currentTime);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (active) ctx.putImageData(active.imageData, 0, 0);
  // annotationCanvasRef and storedAnnotationsRef are stable refs — intentionally omitted from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime]);

  /* Spec 13.11: the shared undo hears when this panel's strokes go, so it
     never offers to undo a stroke that is no longer there. */
  const onHistoryResetRef = useRef(onHistoryReset);
  useEffect(() => { onHistoryResetRef.current = onHistoryReset; });
  // Reset annotation state when video source changes (e.g., modal reopens)
  useEffect(() => {
    onHistoryResetRef.current?.();
    storedAnnotationsRef.current = [];
    annotationTimestampRef.current = 0;
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoDepth(0);
    setRedoDepth(0);
    const canvas = annotationCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.videoUrl]);

  const getCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = annotationCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const applyToolSettings = (ctx: CanvasRenderingContext2D, tool: DrawingTool) => {
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = ERASER_SIZE;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = COLOR_HEX[drawColor];
      ctx.fillStyle = COLOR_HEX[drawColor];
      ctx.lineWidth = BRUSH_SIZE;
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  /* Phone pinch (spec 9.7): a second finger turns a stroke into a gesture.
     The stroke the first finger began is taken back, and nothing is drawn
     until every finger has lifted. */
  const touchPointersRef = useRef(new Set<number>());
  const cancelStroke = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    lastPointRef.current = null;
    const canvas = annotationCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (strokeBaseRef.current) ctx.putImageData(strokeBaseRef.current, 0, 0);
    }
    strokeBaseRef.current = null;
  };

  /* Spec 10.9: two fingers always zoom or pan, never draw. A second finger
     within 120ms of the stroke starting takes the stroke back (it was the
     start of a pinch); later than that, the stroke so far is kept and ends
     there. Either way nothing more is drawn until every finger lifts. */
  const strokeStartedAtRef = useRef(0);
  const onSecondFinger = () => {
    if (!isDrawingRef.current) return;
    if (secondFingerAction(performance.now() - strokeStartedAtRef.current) === 'cancel') cancelStroke();
    else finishStroke();
  };

  /* Spec 15.16: a tap is one finger, under 250ms, never more than 8 (CSS
     px) from where it came down. A stroke starts only past 8, so the two
     never overlap, with any tool. */
  const tapRef = useRef<{ id: number; x: number; y: number; t: number; ok: boolean } | null>(null);
  const strokeArmedRef = useRef(false);
  const strokeStartClientRef = useRef<{ x: number; y: number } | null>(null);
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === 'touch') {
      touchPointersRef.current.add(e.pointerId);
      if (touchPointersRef.current.size > 1) {
        if (tapRef.current) tapRef.current.ok = false;
        onSecondFinger();
        return;
      }
    }
    if (isMobile) tapRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), ok: true };
    if (drawingTool === 'none') return;
    e.preventDefault();
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);

    /* Drawing on a running video pauses it and keeps the stroke, rather than
       refusing it. Only this panel pauses; its partner is a different element
       and is not touched.

       `pause()` takes effect synchronously — it sets `paused` and stops the
       clock before returning, and only the `pause` *event* is deferred — so
       the timestamp read on the next line is the frame the coach is looking
       at. Nothing here waits on an event, so there is no window in which the
       pointer-down could be dropped: the handler runs straight on and captures
       the pointer above, before any of this. */
    const videoEl = videoRef.current;
    /* Phone (spec 11.1): only play and the timeline control playback, so a
       touch on the video never pauses it. */
    if (!isMobile && videoEl && !videoEl.paused) videoEl.pause();

    /* The video's own clock, not the `currentTime` prop. The prop is React
       state fed by timeupdate, which fires about four times a second, so at
       1x it can be up to ~250ms stale — a quarter second of play past the
       frame on screen. Reading the element is exact at any speed. */
    const frameTime = videoEl ? videoEl.currentTime : currentTime;

    // Set up canvas for this drawing session
    const canvas = annotationCanvasRef.current;
    const ctx2d = canvas?.getContext('2d');
    const active = findActiveAnnotation(storedAnnotationsRef.current, frameTime);

    // What undo will step back to, captured before the stroke touches anything.
    strokeBaseRef.current = active ? active.imageData : null;

    if (active) {
      // In range of an existing annotation — restore it as a base and accumulate strokes
      annotationTimestampRef.current = active.timestamp;
      if (ctx2d && canvas) {
        ctx2d.clearRect(0, 0, canvas.width, canvas.height);
        ctx2d.putImageData(active.imageData, 0, 0);
      }
    } else {
      // New annotation at the frame that was on screen when the stroke began
      annotationTimestampRef.current = frameTime;
      if (ctx2d && canvas) ctx2d.clearRect(0, 0, canvas.width, canvas.height);
    }

    isDrawingRef.current = true;
    strokeStartedAtRef.current = performance.now();
    const pt = getCanvasPoint(e);
    lastPointRef.current = pt;
    strokeStartRef.current = pt;
    strokeMovedRef.current = false;
    strokeInsideRef.current = false;
    strokeArmedRef.current = false;
    strokeStartClientRef.current = { x: e.clientX, y: e.clientY };
    /* Phone (spec 13.11, 15.16): nothing is drawn until the finger has
       moved past 8, so a tap leaves no dot; the first segment, from where
       the finger came down, draws the start. */
    if (isMobile) return;
    const ctx = annotationCanvasRef.current?.getContext('2d');
    if (!ctx) return;
    applyToolSettings(ctx, drawingTool);
    ctx.beginPath();
    const radius = drawingTool === 'eraser' ? ERASER_SIZE / 2 : BRUSH_SIZE / 2;
    ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
    ctx.fill();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const tap = tapRef.current;
    if (tap && tap.id === e.pointerId && Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > TAP_SLOP) tap.ok = false;
    if (!isDrawingRef.current || drawingTool === 'none' || touchPointersRef.current.size > 1) return;
    e.preventDefault();
    if (isMobile && !strokeArmedRef.current) {
      const c0 = strokeStartClientRef.current;
      if (c0 && !strokeArmed(Math.hypot(e.clientX - c0.x, e.clientY - c0.y))) return;
      strokeArmedRef.current = true;
    }
    const pt = getCanvasPoint(e);
    const canvasEl = annotationCanvasRef.current;
    const ctx = canvasEl?.getContext('2d');
    if (!ctx || !canvasEl || !lastPointRef.current) return;
    const s0 = strokeStartRef.current;
    if (s0 && Math.hypot(pt.x - s0.x, pt.y - s0.y) >= 3) strokeMovedRef.current = true;
    const within = (q: { x: number; y: number }) => q.x >= 0 && q.y >= 0 && q.x <= canvasEl.width && q.y <= canvasEl.height;
    if (within(pt) || within(lastPointRef.current)) strokeInsideRef.current = true;
    applyToolSettings(ctx, drawingTool);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPointRef.current = pt;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    touchPointersRef.current.delete(e.pointerId);
    const tap = tapRef.current;
    if (tap && tap.id === e.pointerId) {
      tapRef.current = null;
      if (tap.ok && e.type === 'pointerup' && isTap(performance.now() - tap.t, Math.hypot(e.clientX - tap.x, e.clientY - tap.y))) onPhoneTap?.();
    }
    finishStroke();
  };

  /** Ends the stroke in progress and keeps it: the snapshot, undo and the parent's order. */
  const finishStroke = () => {
    /* Phone (spec 13.11): a stroke that shows nothing is put back and not
       kept, so undo only ever has something visible to act on. */
    if (isMobile && isDrawingRef.current) {
      const visible = strokeShows({ moved: strokeMovedRef.current, inside: strokeInsideRef.current, eraser: drawingTool === 'eraser', hadDrawing: strokeBaseRef.current !== null });
      if (!visible) { cancelStroke(); return; }
    }
    const wasDrawing = isDrawingRef.current;
    isDrawingRef.current = false;
    lastPointRef.current = null;
    const canvas = annotationCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) ctx.globalCompositeOperation = 'source-over';
    // Save snapshot — replace the annotation at this timestamp if it exists, otherwise append
    if (wasDrawing && canvas && ctx) {
      const snap: StoredAnnotation = {
        timestamp: annotationTimestampRef.current,
        imageData: ctx.getImageData(0, 0, canvas.width, canvas.height),
      };
      undoStackRef.current = [
        ...undoStackRef.current.slice(-(MAX_UNDO_STEPS - 1)),
        { timestamp: snap.timestamp, imageData: strokeBaseRef.current },
      ];
      redoStackRef.current = [];
      setUndoDepth(undoStackRef.current.length);
      setRedoDepth(0);
      storedAnnotationsRef.current = [
        ...storedAnnotationsRef.current.filter((a) => a.timestamp !== snap.timestamp),
        snap,
      ];
    }
    strokeBaseRef.current = null;
    if (wasDrawing) onStrokeCommitted?.();
  };

  /** Repaint whatever is in range at the current time, the same rule the currentTime effect uses. */
  const repaintForCurrentTime = () => {
    const canvas = annotationCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const active = findActiveAnnotation(storedAnnotationsRef.current, currentTime);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (active) ctx.putImageData(active.imageData, 0, 0);
  };

  const setStoredAt = (timestamp: number, img: ImageData | null) => {
    const rest = storedAnnotationsRef.current.filter((a) => a.timestamp !== timestamp);
    storedAnnotationsRef.current = img ? [...rest, { timestamp, imageData: img }] : rest;
  };

  const storedAt = (timestamp: number) =>
    storedAnnotationsRef.current.find((a) => a.timestamp === timestamp)?.imageData ?? null;

  /** Step one stroke back. The entry carries its own timestamp, so undoing works
      after a scrub as well as immediately after drawing. */
  const handleUndo = (): boolean => {
    const entry = undoStackRef.current[undoStackRef.current.length - 1];
    if (!entry) return false;
    undoStackRef.current = undoStackRef.current.slice(0, -1);
    redoStackRef.current = [
      ...redoStackRef.current,
      { timestamp: entry.timestamp, imageData: storedAt(entry.timestamp) },
    ];
    setStoredAt(entry.timestamp, entry.imageData);
    annotationTimestampRef.current = entry.timestamp;
    repaintForCurrentTime();
    setUndoDepth(undoStackRef.current.length);
    setRedoDepth(redoStackRef.current.length);
    return true;
  };

  const handleRedo = (): boolean => {
    const entry = redoStackRef.current[redoStackRef.current.length - 1];
    if (!entry) return false;
    redoStackRef.current = redoStackRef.current.slice(0, -1);
    undoStackRef.current = [
      ...undoStackRef.current.slice(-(MAX_UNDO_STEPS - 1)),
      { timestamp: entry.timestamp, imageData: storedAt(entry.timestamp) },
    ];
    setStoredAt(entry.timestamp, entry.imageData);
    annotationTimestampRef.current = entry.timestamp;
    repaintForCurrentTime();
    setUndoDepth(undoStackRef.current.length);
    setRedoDepth(redoStackRef.current.length);
    return true;
  };

  /* Only undo and redo are exposed. The snapshots stay in this panel's refs —
     nothing about how they are taken changes. */
  useImperativeHandle(ref, () => ({ undo: handleUndo, redo: handleRedo }));

  const segStart = source.segment?.startTime ?? 0;
  const segEnd = segmentEndOf(source.segment?.endTime ?? null, duration);
  /* Spec 15.17: the side by side scrubber is uncontrolled while a finger is
     on it, so its thumb is the finger's; otherwise it follows the time. */
  const rowScrubRef = useRef<HTMLInputElement>(null);
  const rowReadoutRef = useRef<HTMLSpanElement>(null);
  const rowScrubbingRef = useRef(false);
  useEffect(() => {
    const el = rowScrubRef.current;
    if (el && !rowScrubbingRef.current) el.value = String(toRel(currentTime, { start: segStart, end: segEnd }));
  });
  // When camera source or freeze frame, there are no video controls to show
  const showVideoControls = !isCameraSource && !isFreezeFrame;

  /* Speed and mute belong to a panel, not to the shared tools. Single source
     keeps them in the toolbar row where they have always been; side by side
     moves them to the end of this panel's own transport, because that row is
     no longer per-panel. */
  /* The transport. Rendered here for a single source; side by side the
     parent renders both, below the shared toolbar, so the drawing tools
     sit nearer the video than the playback controls do. */
  const canvasCursor =
    drawingTool === 'none' ? 'default' : drawingTool === 'eraser' ? 'cell' : 'crosshair';

  // canAnnotate for blank/freeze-frame: always allowed (no playing video)
  const canAnnotateEffective = isCameraSource || isFreezeFrame ? true : canAnnotate;

  /* The video, the drawing canvas, the timestamp and the bubble: the same
     in the desktop box and the phone stage. */
  /* Phone pinch zoom (spec 9.7): the video and the drawing canvas zoom as one
     layer, so strokes stay on the court; the timecode chip and the camera
     bubble sit outside it and never zoom. */
  const zoomTargetRef = useRef<HTMLDivElement>(null);
  const drawingToolRef = useRef(drawingTool);
  drawingToolRef.current = drawingTool;
  const panelZoom = usePinchZoom({
    enabled: isMobile && !!phoneBounds,
    surfaceRef: panelAreaRef,
    targetRef: zoomTargetRef,
    onChange: (z) => { if (zoomStateRef) zoomStateRef.current = z; },
    onePanAllowed: () => drawingToolRef.current === 'none',
    onMultiTouch: onSecondFinger,
  });
  const resetPanelZoom = panelZoom.reset;
  useEffect(() => { resetPanelZoom(); }, [source.videoUrl, resetPanelZoom]);

  const mediaLayer = (
    <>
        {/* Camera source: live feed via srcObject set by parent; Normal source: src URL */}
        {isCameraSource ? (
          <video
            ref={setVideoEl}
            autoPlay
            muted
            playsInline
            {...(isMobile ? PHONE_INLINE_EXTRAS : {})}
            className="w-full h-full object-contain"
            style={{ transform: 'scaleX(-1)' }}
          />
        ) : (
          <video
            ref={setVideoEl}
            src={source.videoUrl}
            className="w-full h-full object-contain"
            crossOrigin="anonymous"
            playsInline
            {...(isMobile ? PHONE_INLINE_EXTRAS : {})}
            /* auto on a phone, so a frame is decoded before the first play:
               iOS will otherwise start the audio on play() and leave the
               picture waiting (spec section 8). */
            preload={isMobile ? 'auto' : 'metadata'}
            style={source.rotation ? (() => {
              const needsScale = source.rotation === 90 || source.rotation === 270;
              const scale = needsScale && panelAreaSize.w > 0 && panelAreaSize.h > 0
                ? Math.min(panelAreaSize.w, panelAreaSize.h) / Math.max(panelAreaSize.w, panelAreaSize.h)
                : 1;
              return { transform: `rotate(${source.rotation}deg)${scale !== 1 ? ` scale(${scale})` : ''}` };
            })() : undefined}
          />
        )}
        <canvas
          ref={annotationCanvasRef}
          width={PANEL_W}
          height={PANEL_H}
          style={{ ...annotationCanvasInset, cursor: canvasCursor, touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
    </>
  );

  const stageChildren = (
    <>
        {isMobile && phoneBounds
          ? <div ref={zoomTargetRef} className="absolute inset-0" style={{ willChange: 'transform' }}>{mediaLayer}</div>
          : mediaLayer}
        {/* Timestamp watermark — not shown for camera source. Phone (spec
            14.6): nothing over the video, so not shown on screen; the
            recording still carries its own timestamp. */}
        {!isCameraSource && !phoneStage && (
          <div
            /* Same padding and corner as a speed pill (px-2.5, radius 8), so
               the two chips read as one family. Tenths stay: they are what an
               annotation is keyed to. */
            className="absolute px-2.5 py-0.5 rounded-[8px] text-white text-[11px] font-mono pointer-events-none select-none"
            /* Phone side by side: top-right, since the panel title takes the
               top-left (spec 9.4). */
            style={{ top: phoneRows && phoneStage ? (panelZoom.scale > 1.01 ? 40 : 8) : 12, ...(phoneRows && phoneStage ? { right: 8 } : { left: 12 }), backgroundColor: 'rgba(0,0,0,0.6)', textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}
          >
            {formatTimeDetailed(currentTime)}
          </div>
        )}
        {pipOverlay}
    </>
  );

  if (phoneStage) {
    /* Side by side on a phone (spec 15.10): the title above the video,
       outside it (Subhead 15/20 semibold, one line, as wide as the video); the
       controls in a 44 row directly under it: play, scrubber, current time
       / length, and speed and mute where they fit. */
    const segRange = { start: segStart, end: segEnd };
    /* Spec 15.20: speed and mute are 36 wide in the 44 row. */
    const speedBtn = (size: number) => (
      <SpeedButton size={size} height={REC.target} speed={speed} onCycle={() => onSpeedChange(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])} />
    );
    /* Spec 15.18: while both videos play, mute shows muted and is locked. */
    const shownMuted = isMuted || phoneMuteLocked;
    const muteBtn = (size: number) => (
      <RecButton size={size} height={REC.target} label={phoneMuteLocked ? 'Muted while both videos play' : isMuted ? 'Unmute' : 'Mute'} pressed={shownMuted} onClick={onToggleMute}
        disabled={phoneMuteLocked}
        color={shownMuted ? 'var(--editor-text-muted)' : 'var(--editor-text)'}>
        <MuteGlyph muted={shownMuted} className={size >= 44 ? 'w-6 h-6' : 'w-5 h-5'} />
      </RecButton>
    );
    /* Spec 15.20: the block is as wide as the video, or as wide as its row
       needs (the squeeze case), never wider than the bounds; the video is
       centred in it. */
    const rowMinW = phoneRows ? Math.min(phoneBounds!.maxW, pairRowMinW(readoutChars(segEnd - segStart)) + 2 * phoneRowInset) : 0;
    const blockW = phoneRows ? Math.max(phoneRowInset ? phoneBounds!.maxW : phoneStage.w, rowMinW) : phoneStage.w;
    return (
      <div className="flex flex-col flex-none" style={{ width: blockW }}>
        {phoneRows && (
          <div className="flex-none flex items-end" style={{ height: REC.titleH, marginBottom: REC.titleGap, paddingLeft: phoneTitleIndent || phoneRowInset }} data-testid="sbs-panel-title">
            {source.label && (
              /* Spec 15.19: as wide as its video (less any indent for the X);
                 wider than that, it wraps to two lines and then steps down. Never cut. */
              <FitText lines={2} className="text-[15px] leading-[20px] font-semibold" style={{ maxWidth: Math.max(0, phoneStage.w - phoneTitleIndent), color: 'var(--editor-text)' }} title={source.label}>
                {source.label}{isFreezeFrame ? ' (freeze frame)' : ''}
              </FitText>
            )}
          </div>
        )}
        <div
          ref={panelAreaRef}
          data-phone-stage={phoneStageId ?? 1}
          data-zoom-surface=""
          className="relative bg-black overflow-hidden flex-none self-center"
          style={{
            width: phoneStage.w,
            height: phoneStage.h,
            transition: phoneSettling ? 'width 250ms ease, height 250ms ease' : undefined,
          }}
        >
          <div className={`absolute inset-0 transition-opacity duration-300 ${phoneAspect ? 'opacity-100' : 'opacity-0'}`} style={{ touchAction: 'none' }}>
            {stageChildren}
          </div>
          <ZoomResetChip scale={panelZoom.scale} onReset={panelZoom.reset} />
        </div>
        {/* Spec 15.20: one row directly under the picture, off the video:
            play, scrubber, time, speed, mute. The scrubber takes all the
            width left and never less than 35 percent of the row; speed and
            mute are 36 wide. */}
        {phoneRows && (
              <div
                className="flex items-center flex-none"
                style={{ height: REC.target, gap: REC.gap, paddingLeft: phoneRowInset, paddingRight: phoneRowInset }}
                data-testid="sbs-panel-row"
              >
                {showVideoControls ? (
                  <>
                    <button type="button" onClick={onTogglePlay} className={`${EBTN_TRANSPORT} flex-none`} style={{ color: 'var(--editor-text)' }}
                      title={isPlaying ? 'Pause' : 'Play'} aria-label={isPlaying ? 'Pause' : 'Play'} aria-busy={isLoading || undefined}>
                      {isLoading ? (
                        <span className="w-[18px] h-[18px] rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden="true" />
                      ) : isPlaying ? (
                        <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                      ) : (
                        <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      )}
                    </button>
                    {onPhoneScrub ? (
                      <input
                        ref={rowScrubRef}
                        type="range" min={0} max={segLength(segRange)} step={0.05}
                        defaultValue={toRel(currentTime, segRange)}
                        onPointerDown={() => { rowScrubbingRef.current = true; onPhoneScrub.begin(); }}
                        onChange={(e) => {
                          const t = toAbs(parseFloat(e.target.value), segRange);
                          if (rowScrubbingRef.current) {
                            /* The time under the finger, without a render. */
                            if (rowReadoutRef.current) rowReadoutRef.current.textContent = readout(toRel(t, segRange), segLength(segRange));
                            onPhoneScrub.move(t);
                            return;
                          }
                          onPhoneScrub.begin();
                          onPhoneScrub.end(t);
                        }}
                        onPointerUp={(e) => {
                          if (!rowScrubbingRef.current) return;
                          rowScrubbingRef.current = false;
                          onPhoneScrub.end(toAbs(parseFloat(e.currentTarget.value), segRange));
                        }}
                        onPointerCancel={(e) => {
                          if (!rowScrubbingRef.current) return;
                          rowScrubbingRef.current = false;
                          onPhoneScrub.end(toAbs(parseFloat(e.currentTarget.value), segRange));
                        }}
                        aria-label="Scrub" className="flex-1 min-w-[35%] h-11 lve-phone-range"
                      />
                    ) : (
                      <input
                        type="range" min={0} max={segLength(segRange)} step={0.05}
                        value={toRel(currentTime, segRange)}
                        onChange={(e) => onSeek(toAbs(parseFloat(e.target.value), segRange))}
                        onPointerDown={onScrubStart} onPointerUp={onScrubEnd} onPointerCancel={onScrubEnd}
                        onKeyDown={onScrubStart} onKeyUp={onScrubEnd} onBlur={onScrubEnd}
                        aria-label="Scrub" className="flex-1 min-w-[35%] h-11 lve-phone-range"
                      />
                    )}
                    <span ref={rowReadoutRef} className="flex-none w-max font-mono text-[15px] leading-[20px] text-[var(--editor-text)] tabular-nums" data-testid="segment-readout">
                      {readout(toRel(currentTime, segRange), segLength(segRange))}
                    </span>
                    {speedBtn(REC.pairChip)}
                  </>
                ) : <div className="flex-1" />}
                {muteBtn(REC.pairChip)}
              </div>
        )}
      </div>
    );
  }

  return (
    /* gap-4 (16) between the title, the video and the transport when paired.
       It was gap-1.5 plus the toolbar's own 4px margins and the container's
       padding, which stacked into roughly 72 above and below the picture. */
    <div className={`flex flex-col min-w-0 flex-1 min-h-0 ${sideBySide ? 'gap-4 phone:gap-2' : 'gap-1.5'}`}>
      {/* The name of what is being recorded, not a debug label. Uppercase and
          letter-spacing were what made it read as one; at full strength and in
          sentence case it reads as a title. 13/16 keeps the row at the 16 it
          was, so nothing is taken from the video. */}
      {!source.label && sideBySide && <div className={`${sideBySide ? 'h-6' : 'h-4'} flex-shrink-0`} aria-hidden="true" />}
      {source.label && (
        /* Centred over its own video and larger when paired, so two clips of
           the same stroke are told apart without reading carefully. Paid for
           by the letterbox the box no longer draws. */
        <div className={`flex items-center gap-1.5 px-1 min-w-0 flex-shrink-0 ${sideBySide ? 'justify-center' : ''}`}>
          <FitText lines={2} className={`font-semibold text-[var(--editor-text)] ${sideBySide ? 'text-center text-[15px] leading-6' : 'text-[13px] leading-4'}`}>
            {source.label}
          </FitText>
          {isFreezeFrame && (
            <span className={`text-[var(--editor-text-muted)] flex-shrink-0 ${sideBySide ? 'text-xs leading-6' : 'text-[11px] leading-4'}`}>
              (freeze frame)
            </span>
          )}
        </div>
      )}

      {/* Video / blank / freeze-frame panel + annotation canvas.
          The outer div is the area; the inner one is the box, sized to the
          source so the panel has no letterbox of its own. */}
      <div ref={videoAreaRef} className="flex-1 min-h-0 w-full flex items-center justify-center">
      <div
        ref={panelAreaRef}
        className={`relative bg-black rounded-lg overflow-hidden ${videoBoxSize ? '' : 'w-full h-full'}`}
        style={videoBoxSize ? { width: videoBoxSize.w, height: videoBoxSize.h } : undefined}
      >
        {stageChildren}
      </div>
      </div>

      {/* Single source keeps its own toolbar exactly as it was: one panel, one
          set of tools, nothing to share. Side by side renders the shared row in
          the parent instead. */}
      {!sideBySide && (
        <DrawToolbar
          drawingTool={drawingTool}
          setDrawingTool={onSetDrawingTool}
          drawColor={drawColor}
          setDrawColor={onSetDrawColor}
          canAnnotate={canAnnotateEffective}
          undoDepth={undoDepth}
          redoDepth={redoDepth}
          onUndo={handleUndo}
          onRedo={handleRedo}
        >
          <PanelUtilities showVideoControls={showVideoControls} speed={speed} onSpeedChange={onSpeedChange} isMuted={isMuted} onToggleMute={onToggleMute} />
        </DrawToolbar>
      )}

      {/* Back inside the column, directly under this panel's own video. */}
      {(
        <PanelTransport
          showVideoControls={showVideoControls} sideBySide={sideBySide}
          onScrubStart={onScrubStart} onScrubEnd={onScrubEnd}
          isPlaying={isPlaying} onTogglePlay={onTogglePlay}
          segStart={segStart} segEnd={segEnd} currentTime={currentTime} onSeek={onSeek}
          speed={speed} onSpeedChange={onSpeedChange}
          isMuted={isMuted} onToggleMute={onToggleMute}
        />
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ClipEditorModal({
  isOpen,
  onClose,
  onSave,
  onSaveAndContinue: onSaveAndContinueProp,
  primarySource,
  secondarySource,
  embedded = false,
  onAddSecondarySource,
  initiallyRecording = false,
}: ClipEditorModalProps) {
  const hasTwoSources = !!secondarySource;
  const phoneV = usePhoneLayout();
  const isMobile = phoneV.phone;
  /* For the [] callbacks below. */
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  /* Phone (spec 10.4): a saved take shows "Clip saved" and the recorder
     stays open. The prop is wrapped only to raise the toast; the clip is
     handed over exactly as before. */
  /* Spec 15.27: the take's finaliser and the watchdog that runs it if the
     recorder's stop event never arrives on the phone. */
  const finaliseRef = useRef<(() => void) | null>(null);
  const stopWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [savedToastAt, setSavedToastAt] = useState<number | null>(null);
  const clearSavedToast = useCallback(() => setSavedToastAt(null), []);
  const onSaveAndContinue = useCallback((clip: VoiceoverNotableSegmentClip) => {
    const kept = onSaveAndContinueProp(clip);
    if (!isMobileRef.current) return;
    /* Spec 13.7: "Clip saved" once the take is kept on the device. */
    Promise.resolve(kept).catch(() => {}).then(() => setSavedToastAt(Date.now()));
  }, [onSaveAndContinueProp]);
  /* Phone: the alert asking before a take is lost (spec 10.4). */
  const [phoneAlert, setPhoneAlert] = useState<'back' | 'discard' | null>(null);

  /**
   * Starts a panel's video.
   *
   * On a real iPhone play() has to be called inside the tap: iOS loads no
   * media data before a play, so a seek issued on open never lands, and an
   * unmuted play() from a later event is refused. Waiting for `seeked` before
   * playing (the earlier fix for the frozen first play) therefore never
   * played at all on the phone, though WebKit in Playwright, which loads
   * eagerly, passed it. The frozen first play came from an element with no
   * decoded frame; primeFirstFrame now decodes one when the panel loads, so
   * the phone seeks and plays in the same tap. Desktop is unchanged.
   */
  const startPanelPlayback = useCallback((
    video: HTMLVideoElement,
    wantsRef: React.MutableRefObject<boolean>,
    seekTo: number | null,
    onFail: (err: unknown) => void,
  ) => {
    const go = () => {
      if (!wantsRef.current) return;
      video.play().catch(onFail);
    };
    if (!isMobileRef.current) {
      if (seekTo !== null) video.currentTime = seekTo;
      video.play().catch(onFail);
      return;
    }
    if (seekTo !== null) video.currentTime = seekTo;
    go();
  }, []);

  /**
   * Phone: decode a panel's first frame without a tap. iOS loads only
   * metadata until something plays, so a paused panel stays black and its
   * opening seek never lands. A muted play() is allowed without a gesture;
   * pausing it as soon as it starts leaves the frame painted at the segment
   * start. Skipped when the coach has unmuted (that play would be refused)
   * and when a frame is already there. The pause is skipped if the coach
   * pressed play in the meantime.
   */
  const primeFirstFrame = useCallback((video: HTMLVideoElement, wantsRef: React.MutableRefObject<boolean>) => {
    if (!isMobileRef.current || video.readyState >= 2 || !video.paused || !video.muted) return;
    const p = video.play();
    if (p) p.then(() => { if (!wantsRef.current) video.pause(); }).catch(() => {});
  }, []);

  // Recording state
  const [isRecording, setIsRecording] = useState(initiallyRecording);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const rafIdRef = useRef<number | null>(null);
  const compositeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const canceledRef = useRef(false);
  /** true when the user chose Abort — stop without saving, keep modal open */
  const abortRef = useRef(false);
  /** 'close' → call onSave + onClose; 'continue' → call onSaveAndContinue, keep modal open.
      'unplanned' (phone): set when a take starts, so a take that stops without
      the coach choosing keeps the recorder open (takeOutcome, spec 13.7). */
  const saveModeRef = useRef<'close' | 'continue' | 'unplanned'>('close');
  const audioContextRef = useRef<AudioContext | null>(null);
  const captureStreamsRef = useRef<MediaStream[]>([]);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  const recordingStartMsRef = useRef<number>(0);

  // Camera source stream (when primarySource.isCameraSource — managed by its own useEffect)
  const cameraStreamRef = useRef<MediaStream | null>(null);
  // Secondary camera stream (when secondarySource.isCameraSource — managed by its own useEffect)
  const secondaryCameraStreamRef = useRef<MediaStream | null>(null);

  // PiP camera overlay (picture-in-picture circle on the primary panel during recording)
  const pipCameraVideoRef = useRef<HTMLVideoElement | null>(null);
  // Off-DOM video element used exclusively for canvas drawImage — decoupled from layout
  // so that DOM reflows (e.g. the recording UI appearing) cannot corrupt the captured frame.
  const pipCaptureVideoRef = useRef<HTMLVideoElement | null>(null);
  const pipCameraStreamRef = useRef<MediaStream | null>(null);
  /* On by default, as before; on a phone the coach's last choice (spec 10.7). */
  const [pipEnabled, setPipEnabled] = useState(() => (isMobile ? readPipOn() : true));
  const pipEnabledRef = useRef(pipEnabled);
  const panelsContainerRef = useRef<HTMLDivElement>(null);

  /**
   * The displayed video's content box, in pixels relative to the panels
   * container. This is the space the bubble now lives in.
   *
   * Derived with the same contain maths the composite canvas uses, seeded by a
   * measured element box. Pure derivation is not cleaner here: the element's
   * own size depends on the flex layout, the label row and the container's
   * padding, so the DOM has to be measured either way. Only the contain step
   * is computed.
   *
   * Side by side is deliberately the whole container: that canvas is still a
   * fixed 2560x720 with each source contained inside its half, so the bars are
   * part of the canvas there and the container is the matching space.
   */
  const [dividerHeight, setDividerHeight] = useState(0);

  /* ── Shared drawing tools ────────────────────────────────────────────────
     The tool and the colour live here so both panels answer to one control.
     The ImageData snapshots do not move: each panel keeps its own, and this
     holds nothing but the order strokes were committed in, so one Undo can
     step back through both panels most-recent-first. */
  const [drawingTool, setDrawingTool] = useState<DrawingTool>('none');
  const [drawColor, setDrawColor] = useState<DrawColor>('red');
  const panel1Ref = useRef<VideoPanelHandle>(null);
  const panel2Ref = useRef<VideoPanelHandle>(null);
  const strokeOrderRef = useRef<(1 | 2)[]>([]);
  const redoOrderRef = useRef<(1 | 2)[]>([]);
  const [historyDepth, setHistoryDepth] = useState({ undo: 0, redo: 0 });

  const noteStroke = useCallback((panel: 1 | 2) => {
    /* Capped to match each panel's own MAX_UNDO_STEPS so the list cannot grow
       far past what the panels can actually step back through. */
    strokeOrderRef.current = [...strokeOrderRef.current.slice(-(MAX_UNDO_STEPS * 2 - 1)), panel];
    redoOrderRef.current = [];
    setHistoryDepth({ undo: strokeOrderRef.current.length, redo: 0 });
  }, []);

  /* Spec 13.11: a panel's strokes went (a new source): its entries go too. */
  const dropPanelHistory = useCallback((panel: 1 | 2) => {
    strokeOrderRef.current = strokeOrderRef.current.filter((p) => p !== panel);
    redoOrderRef.current = redoOrderRef.current.filter((p) => p !== panel);
    setHistoryDepth({ undo: strokeOrderRef.current.length, redo: redoOrderRef.current.length });
  }, []);
  const dropPanel1History = useCallback(() => dropPanelHistory(1), [dropPanelHistory]);
  const dropPanel2History = useCallback(() => dropPanelHistory(2), [dropPanelHistory]);

  const handleSharedUndo = useCallback(() => {
    /* A panel's own stack is capped at 10, so an entry here can outlive the
       snapshot it refers to. undo() reports that, and we walk on to the next
       most recent stroke rather than doing nothing. */
    while (strokeOrderRef.current.length > 0) {
      const panel = strokeOrderRef.current[strokeOrderRef.current.length - 1];
      strokeOrderRef.current = strokeOrderRef.current.slice(0, -1);
      const did = panel === 1 ? panel1Ref.current?.undo() : panel2Ref.current?.undo();
      if (did) {
        redoOrderRef.current = [...redoOrderRef.current, panel];
        break;
      }
    }
    setHistoryDepth({ undo: strokeOrderRef.current.length, redo: redoOrderRef.current.length });
  }, []);

  const handleSharedRedo = useCallback(() => {
    while (redoOrderRef.current.length > 0) {
      const panel = redoOrderRef.current[redoOrderRef.current.length - 1];
      redoOrderRef.current = redoOrderRef.current.slice(0, -1);
      const did = panel === 1 ? panel1Ref.current?.redo() : panel2Ref.current?.redo();
      if (did) {
        strokeOrderRef.current = [...strokeOrderRef.current, panel];
        break;
      }
    }
    setHistoryDepth({ undo: strokeOrderRef.current.length, redo: redoOrderRef.current.length });
  }, []);

  const [videoBox, setVideoBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  /* Spec 13.9: if a panel's <video> is ever replaced while open (it should
     not be: one tree for both orientations), its listeners and camera are
     re-attached and it goes back to where it was. vNGen is bumped only for a
     replacement, never for the first element. */
  const [v1Gen, setV1Gen] = useState(0);
  const [v2Gen, setV2Gen] = useState(0);
  const seenV1Ref = useRef<HTMLVideoElement | null>(null);
  const seenV2Ref = useRef<HTMLVideoElement | null>(null);
  const recover1Ref = useRef(false);
  const recover2Ref = useRef(false);
  const onVideo1Element = useCallback((el: HTMLVideoElement | null) => {
    if (!el || el === seenV1Ref.current) return;
    const replaced = seenV1Ref.current !== null;
    seenV1Ref.current = el;
    if (replaced) { recover1Ref.current = true; setV1Gen((g) => g + 1); }
  }, []);
  const onVideo2Element = useCallback((el: HTMLVideoElement | null) => {
    if (!el || el === seenV2Ref.current) return;
    const replaced = seenV2Ref.current !== null;
    seenV2Ref.current = el;
    if (replaced) { recover2Ref.current = true; setV2Gen((g) => g + 1); }
  }, []);

  // Panel 1 refs + state
  const video1Ref = useRef<HTMLVideoElement>(null);
  const annotationCanvas1Ref = useRef<HTMLCanvasElement>(null);
  /* Spec 13.8: the play button follows the element's own events (playbackState.ts). */
  const [p1Ui, setP1Ui] = useState<PlaybackUi>('paused');
  const p1IsPlaying = p1Ui !== 'paused';
  const [p1CurrentTime, setP1CurrentTime] = useState(primarySource.segment?.startTime ?? 0);
  const [p1Duration, setP1Duration] = useState(0);
  const [p1Speed, setP1Speed] = useState<Speed>(1);
  const p1SpeedRef = useRef<Speed>(1);
  const [p1Muted, setP1Muted] = useState(true);
  const p1MutedRef = useRef(true);
  // Ref so drawFrame (rAF closure) can always read the latest current time
  const p1CurrentTimeRef = useRef<number>(primarySource.segment?.startTime ?? 0);
  // Stable ref to the segment start — readable in handleTogglePlay1 ([] deps callback)
  const p1SegStartRef = useRef<number>(primarySource.segment?.startTime ?? 0);
  // Infinity when no segment (currentTime >= Infinity is always false — no restart logic)
  /* null means "no segment": the end of the range is the end of the video,
     which is only known once metadata has loaded. Resolved at call time by
     segmentEndOf() rather than frozen to a number here. */
  const p1SegEndRef = useRef<number | null>(primarySource.segment?.endTime ?? null);
  // Latest scrub position queued while a seek is already in progress
  const p1PendingSeekRef = useRef<number | null>(null);
  /** Phone, side by side (spec 15.17): a finger is on this panel's scrubber. */
  const p1ScrubbingRef = useRef(false);
  /* What the coach last asked this panel to do, which is not the same question
     as `video.paused`.

     `play()` flips `paused` to false synchronously, long before any data
     arrives, so on a cold video a second click read "already playing" and
     called pause() — cancelling the load it was trying to start. Alternating
     clicks cancelled each other, which is what "it took ten clicks" was.

     Intent lives here; reality lives on the element. Reality always wins: the
     `pause` listener below clears this on every pause whatever caused it, so
     the two cannot drift. */
  const p1WantsPlayRef = useRef(false);

  // Panel 2 refs + state
  const video2Ref = useRef<HTMLVideoElement>(null);
  const annotationCanvas2Ref = useRef<HTMLCanvasElement>(null);
  const [p2Ui, setP2Ui] = useState<PlaybackUi>('paused');
  const p2IsPlaying = p2Ui !== 'paused';
  const [p2CurrentTime, setP2CurrentTime] = useState(secondarySource?.segment?.startTime ?? 0);
  const [p2Duration, setP2Duration] = useState(0);
  const [p2Speed, setP2Speed] = useState<Speed>(1);
  const p2SpeedRef = useRef<Speed>(1);
  const [p2Muted, setP2Muted] = useState(true);
  const p2MutedRef = useRef(true);
  const p2CurrentTimeRef = useRef<number>(secondarySource?.segment?.startTime ?? 0);
  // Stable ref to the segment start — readable in handleTogglePlay2 ([] deps callback)
  const p2SegStartRef = useRef<number>(secondarySource?.segment?.startTime ?? 0);
  const p2SegEndRef = useRef<number | null>(secondarySource?.segment?.endTime ?? null);
  const p2PendingSeekRef = useRef<number | null>(null);
  const p2ScrubbingRef = useRef(false);
  /* Spec 15.18: both side by side videos playing means both muted. */
  const hasTwoRef = useRef(hasTwoSources);
  useEffect(() => { hasTwoRef.current = hasTwoSources; });
  const pairMuteRefs = useRef<PairMuteRefs>({ phone: isMobileRef, two: hasTwoRef, v1: video1Ref, v2: video2Ref, m1: p1MutedRef, m2: p2MutedRef, s1: p1SpeedRef, s2: p2SpeedRef });
  // See the note on p1WantsPlayRef.
  const p2WantsPlayRef = useRef(false);

  /**
   * Phone (spec 13.8): iOS can pause the page's videos when the camera and
   * microphone start. Whatever was playing (or asked to) before the stream
   * starts is played again once it has. If iOS refuses that play, the button
   * shows play and the refusal is noted in the debug overlay.
   */
  const keepPlayingThrough = useCallback(async (start: () => Promise<MediaStream>): Promise<MediaStream> => {
    const v1 = video1Ref.current;
    const v2 = video2Ref.current;
    const r1 = !!v1 && (p1WantsPlayRef.current || !v1.paused);
    const r2 = !!v2 && (p2WantsPlayRef.current || !v2.paused);
    const stream = await start();
    const resume = (v: HTMLVideoElement | null, wants: React.MutableRefObject<boolean>, label: string) => {
      if (!v || !v.paused) return;
      wants.current = true;
      v.play().catch((err) => { wants.current = false; debugNote(`${label} play() after the camera started refused: ${err?.name ?? err}`); });
    };
    if (r1) resume(video1Ref.current, p1WantsPlayRef, 'panel 1');
    if (r2) resume(video2Ref.current, p2WantsPlayRef, 'panel 2');
    return stream;
  }, []);

  /* One control, one answer. Enabled when either panel could keep a stroke;
     a panel that could not simply refuses the pointer. */
  const p1CanAnnotate = primarySource.isCameraSource === true
    || (!!primarySource.segment && primarySource.segment.startTime === primarySource.segment.endTime)
    || !p1IsPlaying;
  const p2CanAnnotate = !secondarySource ? false : (secondarySource.isCameraSource === true
    || (!!secondarySource.segment && secondarySource.segment.startTime === secondarySource.segment.endTime)
    || !p2IsPlaying);
  const sharedCanAnnotate = p1CanAnnotate || p2CanAnnotate;

  // Reset state when modal opens / source changes
  const initialRenderRef = useRef(true);
  useEffect(() => {
    if (!isOpen) return;
    // Skip recording reset on first render if initiallyRecording is true
    if (initialRenderRef.current && initiallyRecording) {
      initialRenderRef.current = false;
    } else if (mediaRecorderRef.current === null) {
      // Don't reset isRecording if the recorder is mid-finalization (onstop hasn't fired yet).
      // primarySource can change synchronously from onSaveAndContinue inside onstop — if
      // we reset isRecording here before onstop finishes assembling the blob, and the user
      // immediately starts a new recording, chunksRef gets cleared before the old blob is built.
      setIsRecording(false);
    }
    canceledRef.current = false;
  }, [isOpen, primarySource, secondarySource]);

  /* Per-panel reset, split in two.
     One effect used to reset BOTH panels on `[isOpen, primarySource,
     secondarySource]`, which made adding a comparison source mid-session
     rewind panel 1, drop its speed ref to 1 and re-mute it — while the element
     itself kept playing at whatever it was, so the refs and the video
     disagreed from then on. Each panel now resets only when its own source
     changes. */
  useEffect(() => {
    if (!isOpen) return;
    const p1Start = primarySource.segment?.startTime ?? 0;
    setP1CurrentTime(p1Start);
    p1CurrentTimeRef.current = p1Start;
    p1SegStartRef.current = p1Start;
    p1SegEndRef.current = primarySource.segment?.endTime ?? null;
    p1PendingSeekRef.current = null;
    /* A new src runs the element's load algorithm, which sets `paused` to true
       WITHOUT firing a `pause` event — so the listener that reconciles this ref
       never sees it. Cleared here with the rest of this panel's state. */
    p1WantsPlayRef.current = false;
    setP1Speed(1);
    p1SpeedRef.current = 1;
    setP1Muted(true);
    p1MutedRef.current = true;
  }, [isOpen, primarySource]);

  useEffect(() => {
    if (!isOpen) return;
    const p2Start = secondarySource?.segment?.startTime ?? 0;
    setP2CurrentTime(p2Start);
    p2CurrentTimeRef.current = p2Start;
    p2SegStartRef.current = p2Start;
    p2SegEndRef.current = secondarySource?.segment?.endTime ?? null;
    p2PendingSeekRef.current = null;
    // See the note in panel 1's reset.
    p2WantsPlayRef.current = false;
    setP2Speed(1);
    p2SpeedRef.current = 1;
    setP2Muted(true);
    p2MutedRef.current = true;
  }, [isOpen, secondarySource]);

  // Camera stream acquisition — runs when modal opens with a camera primary source
  useEffect(() => {
    if (!isOpen || !primarySource.isCameraSource) return;
    let cancelled = false;
    /* Phone (spec 13.6): the session's one stream, never stopped here. */
    if (isMobileRef.current) {
      acquireCapture()
        .then((stream) => { if (!cancelled && video1Ref.current) video1Ref.current.srcObject = stream; })
        .catch((err) => console.error('ClipEditorModal: camera access failed', err));
      return () => {
        cancelled = true;
        if (video1Ref.current) video1Ref.current.srcObject = null;
      };
    }
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        cameraStreamRef.current = stream;
        if (video1Ref.current) video1Ref.current.srcObject = stream;
      })
      .catch((err) => console.error('ClipEditorModal: camera access failed', err));
    return () => {
      cancelled = true;
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
      if (video1Ref.current) video1Ref.current.srcObject = null;
    };
  }, [isOpen, primarySource.isCameraSource, v1Gen]);

  // Secondary camera stream acquisition — runs when modal opens with a camera secondary source
  useEffect(() => {
    if (!isOpen || !secondarySource?.isCameraSource) return;
    let cancelled = false;
    /* Phone (spec 13.6): the session's one stream, never stopped here. */
    if (isMobileRef.current) {
      acquireCapture()
        .then((stream) => {
          if (cancelled || !video2Ref.current) return;
          video2Ref.current.srcObject = stream;
          video2Ref.current.play().catch(() => {});
        })
        .catch((err) => console.error('ClipEditorModal: secondary camera access failed', err));
      return () => {
        cancelled = true;
        if (video2Ref.current) video2Ref.current.srcObject = null;
      };
    }
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        secondaryCameraStreamRef.current = stream;
        if (video2Ref.current) {
          video2Ref.current.srcObject = stream;
          video2Ref.current.play().catch(() => {});
        }
      })
      .catch((err) => console.error('ClipEditorModal: secondary camera access failed', err));
    return () => {
      cancelled = true;
      secondaryCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      secondaryCameraStreamRef.current = null;
      if (video2Ref.current) video2Ref.current.srcObject = null;
    };
  }, [isOpen, secondarySource?.isCameraSource, v2Gen]);

  // PiP camera stream — acquired/released when the PiP checkbox is toggled
  useEffect(() => {
    pipEnabledRef.current = pipEnabled;
    /* Phone (spec 13.6): the bubble shows the session's one stream. Turning
       it off detaches it and stops nothing. */
    if (isMobileRef.current) {
      if (!pipEnabled) {
        if (pipCaptureVideoRef.current) pipCaptureVideoRef.current.srcObject = null;
        pipCaptureVideoRef.current = null;
        if (pipCameraVideoRef.current) pipCameraVideoRef.current.srcObject = null;
        return;
      }
      let cancelled = false;
      keepPlayingThrough(acquireCapture)
        .then((stream) => {
          if (cancelled) return;
          const captureEl = document.createElement('video');
          captureEl.srcObject = stream;
          captureEl.autoplay = true;
          captureEl.muted = true;
          captureEl.playsInline = true;
          captureEl.play().catch(() => {});
          pipCaptureVideoRef.current = captureEl;
          if (pipCameraVideoRef.current) {
            pipCameraVideoRef.current.srcObject = stream;
            pipCameraVideoRef.current.play().catch(() => {});
          }
        })
        .catch((err) => {
          console.error('ClipEditorModal: camera access failed', err);
          setPipEnabled(false);
        });
      return () => { cancelled = true; };
    }
    if (!pipEnabled) {
      pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      pipCameraStreamRef.current = null;
      pipCaptureVideoRef.current = null;
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: true })
      .then((stream) => {
        pipCameraStreamRef.current = stream;
        // Off-DOM capture element — never in the layout, so layout reflows (e.g. the
        // recording UI appearing) cannot corrupt the frame returned by drawImage.
        const captureEl = document.createElement('video');
        captureEl.srcObject = stream;
        captureEl.autoplay = true;
        captureEl.muted = true;
        captureEl.playsInline = true;
        captureEl.play().catch(() => {});
        pipCaptureVideoRef.current = captureEl;
        // pipCameraVideoRef may or may not be mounted yet — the callback ref handles late binding
        if (pipCameraVideoRef.current) {
          pipCameraVideoRef.current.srcObject = stream;
          pipCameraVideoRef.current.play().catch(() => {});
        }
      })
      .catch((err) => {
        console.error('ClipEditorModal: PiP camera access failed', err);
        setPipEnabled(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipEnabled]);

  // Callback ref for the PiP video element — sets srcObject immediately on mount
  // so the live feed starts even if the stream arrived before the element rendered.
  const setPipVideoRef = useCallback((el: HTMLVideoElement | null) => {
    pipCameraVideoRef.current = el;
    const stream = isMobileRef.current ? currentCapture() : pipCameraStreamRef.current;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      el.play().catch(() => {});
    }
  }, []);

  /* Phone (spec 13.6): when iOS ended the stream and a new one was granted,
     everything showing the camera takes the new one. */
  useEffect(() => {
    if (!isOpen || !isMobile) return;
    return onCaptureChange((stream) => {
      if (!stream) return;
      if (primarySource.isCameraSource && video1Ref.current) video1Ref.current.srcObject = stream;
      if (secondarySource?.isCameraSource && video2Ref.current) video2Ref.current.srcObject = stream;
      if (pipEnabledRef.current) {
        if (pipCaptureVideoRef.current) { pipCaptureVideoRef.current.srcObject = stream; pipCaptureVideoRef.current.play().catch(() => {}); }
        if (pipCameraVideoRef.current) { pipCameraVideoRef.current.srcObject = stream; pipCameraVideoRef.current.play().catch(() => {}); }
      }
    });
  }, [isOpen, isMobile, primarySource.isCameraSource, secondarySource?.isCameraSource]);

  // Panel 1 video events
  useEffect(() => {
    if (primarySource.isCameraSource) return; // camera feed has no seekable timeline
    const video = video1Ref.current;
    if (!video || !isOpen) return;

    const handleLoaded = () => {
      /* A replaced element (spec 13.9) goes back to where the panel was. */
      const recovering = recover1Ref.current;
      recover1Ref.current = false;
      const t = recovering ? p1CurrentTimeRef.current : (primarySource.segment?.startTime ?? 0);
      setP1Duration(video.duration);
      p1CurrentTimeRef.current = t;
      setP1CurrentTime(t);
      // Apply current speed/mute state immediately — user may have changed them before
      // the video element was ready.
      applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
      syncPairMutes(pairMuteRefs.current);
      // Pre-warm: seek to segment start so the first play is instant (no cold range
      // request on click).  The seek→timeupdate→seek infinite loop that previously
      // caused CPU spin is now prevented by the `if (video.seeking) return` guard
      // in handleTimeUpdate, so this seek fires exactly once.
      if (t > 0) video.currentTime = t;
      if (recovering && p1WantsPlayRef.current) video.play().catch(() => { p1WantsPlayRef.current = false; });
      else primeFirstFrame(video, p1WantsPlayRef);
    };

    /* Phone (spec 11.1): the segment's range, enforced every frame while
       playing as well as on timeupdate, which only fires about four times a
       second and let playback run on past the end. */
    const phoneRange = isMobileRef.current && primarySource.segment ? { start: primarySource.segment.startTime, end: primarySource.segment.endTime } : null;
    let guardRaf = 0;
    /** Stop on the end frame: pause, and seek only if it overshot. */
    const parkAtEnd = () => {
      if (!phoneRange) return;
      if (!video.paused) video.pause();
      const to = parkAt(video.currentTime, phoneRange);
      if (to !== null) video.currentTime = to;
      setP1Ui('paused');
      p1CurrentTimeRef.current = phoneRange.end;
      setP1CurrentTime(phoneRange.end);
    };
    const guard = () => {
      guardRaf = 0;
      if (!phoneRange || video.paused) return;
      if (!video.seeking && outOfRange(video.currentTime, phoneRange) === 'after') { parkAtEnd(); return; }
      guardRaf = requestAnimationFrame(guard);
    };

    const handleTimeUpdate = () => {
      // Bail while a seek is in progress — prevents seek → timeupdate → seek loop
      if (video.seeking) return;
      if (p2ScrubbingRef.current) return;
      /* Spec 15.17: while a finger is on this panel's scrubber the finger
         leads; nothing writes the element's lagging time back. */
      if (p1ScrubbingRef.current) return;
      if (phoneRange) {
        const out = outOfRange(video.currentTime, phoneRange);
        if (out === 'before') { video.currentTime = phoneRange.start; return; }
        if (out === 'after') { parkAtEnd(); return; }
        p1CurrentTimeRef.current = video.currentTime;
        setP1CurrentTime(video.currentTime);
        return;
      }
      const seg = primarySource.segment;
      if (seg) {
        // Use SEGMENT_START_EPSILON here: after a seek to seg.startTime, floating-point
        // representation can make video.currentTime land a tiny fraction below the target.
        // Without the epsilon, this fires another seek → seeked → timeupdate → seek loop
        // which keeps the CPU busy and also aborts any pending play() promise.
        if (video.currentTime < seg.startTime - SEGMENT_START_EPSILON) {
          video.currentTime = seg.startTime;
          return;
        }
        if (video.currentTime >= seg.endTime) {
          video.pause();
          video.currentTime = seg.endTime;
          setP1Ui('paused');
          p1CurrentTimeRef.current = seg.endTime;
          setP1CurrentTime(seg.endTime);
          /* Panel 2 is not touched. The panels are independent: one reaching
             the end of its segment says nothing about the other, which may be
             a different video of a different length that the coach is still
             watching. This used to call video2Ref.current?.pause().

             It read as symmetric with the matching line in panel 2's handler,
             and was not. Both sit inside `if (seg)`, and a comparison source
             picked from the drill library carries no segment — so panel 2
             could never pause panel 1, while panel 1 paused panel 2 every
             time. Worse, `timeupdate` fires on a currentTime assignment and
             not only during playback, so once panel 1 was parked at its
             segment end any later seek on it re-entered this branch and killed
             panel 2 again. */
          return;
        }
      }
      p1CurrentTimeRef.current = video.currentTime;
      setP1CurrentTime(video.currentTime);
    };

    const signal1 = (sig: MediaSignal) => setP1Ui((u) => nextPlaybackUi(u, sig, video));
    const onPlaying1 = () => signal1('playing');
    const onWaiting1 = () => signal1('waiting');
    const onEnded1 = () => { signal1('ended'); syncPairMutes(pairMuteRefs.current); };
    /* Spec 13.8: a play asked for before the video could play starts here. */
    const onCanPlay1 = () => {
      signal1('canplay');
      if (isMobileRef.current && p1WantsPlayRef.current && video.paused) video.play().catch((err) => { p1WantsPlayRef.current = false; signal1('rejected'); debugNote(`panel 1 play() refused: ${err?.name ?? err}`); });
    };
    const handlePlay = () => {
      signal1('play');
      // Re-apply speed/mute after play starts — some browsers reset playbackRate
      // during play() or when it was set before the video was in a playable state.
      applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
      syncPairMutes(pairMuteRefs.current);
      if (phoneRange && !guardRaf) guardRaf = requestAnimationFrame(guard);
    };
    /* The one place intent is reconciled with reality. Every way this panel can
       stop without a click goes through a real `pause` event — segment end,
       auto-pause on draw, recording stop/cancel/unmount/close, a media error,
       an OS or media-key pause — so clearing the intent here closes all of
       them at once. Without this the ref would still claim the panel wants to
       play and the next click would pause an already-paused element, which is
       the same dead click in a different costume. */
    const handlePause = () => {
      p1WantsPlayRef.current = false;
      setP1Ui('paused');
      syncPairMutes(pairMuteRefs.current);
      /* Phone: the opening frame-prime plays from wherever iOS put the
         element, which can be the top of the video; put it back inside. */
      if (phoneRange && !p1ScrubbingRef.current && !video.seeking && outOfRange(video.currentTime, phoneRange) === 'before') video.currentTime = phoneRange.start;
    };

    // On seek complete: apply any scrub position queued while this seek was in progress.
    // No muted/speed restoration here — those are set atomically via applyVideoState
    // at the call sites (handleTogglePlay1, handleSpeedChange1, handleLoaded) so there
    // is no race between seeking events and the play() call.
    const handleSeeked = () => {
      const pendingT = p1PendingSeekRef.current;
      if (pendingT !== null) {
        p1PendingSeekRef.current = null;
        video.currentTime = pendingT;
      }
    };

    video.addEventListener('loadedmetadata', handleLoaded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('playing', onPlaying1);
    video.addEventListener('waiting', onWaiting1);
    video.addEventListener('ended', onEnded1);
    video.addEventListener('canplay', onCanPlay1);
    /* What the element is doing now, not what it was before these listeners. */
    setP1Ui(video.paused ? 'paused' : video.readyState < 2 ? 'loading' : 'playing');

    // If the video is already loaded (e.g., cached from a previous open),
    // loadedmetadata won't fire again — initialise manually.
    if (video.readyState >= 1) handleLoaded();

    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('playing', onPlaying1);
      video.removeEventListener('waiting', onWaiting1);
      video.removeEventListener('ended', onEnded1);
      video.removeEventListener('canplay', onCanPlay1);
      if (guardRaf) cancelAnimationFrame(guardRaf);
    };
  }, [isOpen, primarySource, primeFirstFrame, v1Gen]);

  // Panel 2 video events
  useEffect(() => {
    if (!secondarySource) return;
    const video = video2Ref.current;
    if (!video || !isOpen) return;

    const handleLoaded = () => {
      const recovering = recover2Ref.current;
      recover2Ref.current = false;
      const t = recovering ? p2CurrentTimeRef.current : (secondarySource.segment?.startTime ?? 0);
      setP2Duration(video.duration);
      p2CurrentTimeRef.current = t;
      setP2CurrentTime(t);
      applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
      syncPairMutes(pairMuteRefs.current);
      if (t > 0) video.currentTime = t;
      if (recovering && p2WantsPlayRef.current) video.play().catch(() => { p2WantsPlayRef.current = false; });
      else if (!secondarySource.isCameraSource) primeFirstFrame(video, p2WantsPlayRef);
    };

    /* Phone (spec 11.1): the segment's range, enforced every frame while
       playing as well as on timeupdate, which only fires about four times a
       second and let playback run on past the end. */
    const phoneRange = isMobileRef.current && secondarySource.segment ? { start: secondarySource.segment.startTime, end: secondarySource.segment.endTime } : null;
    let guardRaf = 0;
    /** Stop on the end frame: pause, and seek only if it overshot. */
    const parkAtEnd = () => {
      if (!phoneRange) return;
      if (!video.paused) video.pause();
      const to = parkAt(video.currentTime, phoneRange);
      if (to !== null) video.currentTime = to;
      setP2Ui('paused');
      p2CurrentTimeRef.current = phoneRange.end;
      setP2CurrentTime(phoneRange.end);
    };
    const guard = () => {
      guardRaf = 0;
      if (!phoneRange || video.paused) return;
      if (!video.seeking && outOfRange(video.currentTime, phoneRange) === 'after') { parkAtEnd(); return; }
      guardRaf = requestAnimationFrame(guard);
    };

    const handleTimeUpdate = () => {
      // Bail while a seek is in progress — prevents seek → timeupdate → seek loop
      if (video.seeking) return;
      if (phoneRange) {
        const out = outOfRange(video.currentTime, phoneRange);
        if (out === 'before') { video.currentTime = phoneRange.start; return; }
        if (out === 'after') { parkAtEnd(); return; }
        p2CurrentTimeRef.current = video.currentTime;
        setP2CurrentTime(video.currentTime);
        return;
      }
      const seg = secondarySource.segment;
      if (seg) {
        if (video.currentTime < seg.startTime - SEGMENT_START_EPSILON) {
          video.currentTime = seg.startTime;
          return;
        }
        if (video.currentTime >= seg.endTime) {
          video.pause();
          video.currentTime = seg.endTime;
          setP2Ui('paused');
          p2CurrentTimeRef.current = seg.endTime;
          setP2CurrentTime(seg.endTime);
          // Panel 1 is not touched. See the matching note in panel 1's handler.
          return;
        }
      }
      p2CurrentTimeRef.current = video.currentTime;
      setP2CurrentTime(video.currentTime);
    };

    const signal2 = (sig: MediaSignal) => setP2Ui((u) => nextPlaybackUi(u, sig, video));
    const onPlaying2 = () => signal2('playing');
    const onWaiting2 = () => signal2('waiting');
    const onEnded2 = () => { signal2('ended'); syncPairMutes(pairMuteRefs.current); };
    const onCanPlay2 = () => {
      signal2('canplay');
      if (isMobileRef.current && p2WantsPlayRef.current && video.paused) video.play().catch((err) => { p2WantsPlayRef.current = false; signal2('rejected'); debugNote(`panel 2 play() refused: ${err?.name ?? err}`); });
    };
    const handlePlay = () => {
      signal2('play');
      applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
      syncPairMutes(pairMuteRefs.current);
      if (phoneRange && !guardRaf) guardRaf = requestAnimationFrame(guard);
    };
    // See the note in panel 1's handlePause.
    const handlePause = () => {
      p2WantsPlayRef.current = false;
      setP2Ui('paused');
      syncPairMutes(pairMuteRefs.current);
      if (phoneRange && !p2ScrubbingRef.current && !video.seeking && outOfRange(video.currentTime, phoneRange) === 'before') video.currentTime = phoneRange.start;
    };

    const handleSeeked = () => {
      const pendingT = p2PendingSeekRef.current;
      if (pendingT !== null) {
        p2PendingSeekRef.current = null;
        video.currentTime = pendingT;
      }
    };

    video.addEventListener('loadedmetadata', handleLoaded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('playing', onPlaying2);
    video.addEventListener('waiting', onWaiting2);
    video.addEventListener('ended', onEnded2);
    video.addEventListener('canplay', onCanPlay2);
    setP2Ui(video.paused ? 'paused' : video.readyState < 2 ? 'loading' : 'playing');

    // If the video is already loaded (e.g., cached from a previous open),
    // loadedmetadata won't fire again — initialise manually.
    if (video.readyState >= 1) handleLoaded();

    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('playing', onPlaying2);
      video.removeEventListener('waiting', onWaiting2);
      video.removeEventListener('ended', onEnded2);
      video.removeEventListener('canplay', onCanPlay2);
      if (guardRaf) cancelAnimationFrame(guardRaf);
    };
  }, [isOpen, secondarySource, primeFirstFrame, v2Gen]);

  // Panel 1 handlers — each panel is fully independent
  const handleTogglePlay1 = useCallback(() => {
    const video = video1Ref.current;
    if (!video) return;
    /* Branch on intent, not on `video.paused`. While a cold video is still
       buffering `paused` is already false, so this used to pause it. */
    if (p1WantsPlayRef.current) {
      p1WantsPlayRef.current = false;
      video.pause();
      return;
    }
    p1WantsPlayRef.current = true;

    const segStart = p1SegStartRef.current;
    const segEnd = segmentEndOf(p1SegEndRef.current, video.duration);

    // Seek to segment start if we're at-or-before it, or at/past the end.
    // Using <= (not <) for the lower bound: when currentTime is exactly at segStart
    // the pre-warm seek may have just completed and floating-point state may not be
    // fully settled.  Re-seeking ensures a clean position and queues play() safely
    // while the seek is in flight.  The browser handles play-while-seeking natively.
    const atEnd = video.currentTime >= segEnd - (isMobileRef.current ? SEGMENT_END_EPSILON_PHONE : 0);
    const seekTo = video.currentTime <= segStart || atEnd ? segStart : null;

    applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
    syncPairMutes(pairMuteRefs.current, 1);
    startPanelPlayback(video, p1WantsPlayRef, seekTo, (err) => {
      /* A rejected play() leaves the element paused without firing `pause`, so
         this is the one stop the listener above cannot see. Clearing here keeps
         the next click a play rather than a no-op pause. An AbortError is a
         load or seek interrupting it: the wish stands and canplay starts it. */
      const name = (err as { name?: string } | null)?.name;
      if (name === 'AbortError' && isMobileRef.current) return;
      p1WantsPlayRef.current = false;
      setP1Ui('paused');
      debugNote(`panel 1 play() refused: ${name ?? err}`);
    });
  }, [startPanelPlayback]);

  const handleSeek1 = useCallback((t: number) => {
    p1CurrentTimeRef.current = t;
    setP1CurrentTime(t);
    const video = video1Ref.current;
    if (!video) return;
    if (video.seeking) {
      // Another seek in progress — queue this position; handleSeeked will apply it
      p1PendingSeekRef.current = t;
    } else {
      video.currentTime = t;
    }
  }, []);

  const handleSpeedChange1 = useCallback((s: Speed) => {
    p1SpeedRef.current = s;
    if (video1Ref.current) applyVideoState(video1Ref.current, s, p1MutedRef.current);
    syncPairMutes(pairMuteRefs.current);
    setP1Speed(s);
  }, []);

  const handleToggleMute1 = useCallback(() => {
    const next = !p1MutedRef.current;
    p1MutedRef.current = next;
    setP1Muted(next);
    if (video1Ref.current) applyVideoState(video1Ref.current, p1SpeedRef.current, next);
    syncPairMutes(pairMuteRefs.current);
  }, []);

  // Panel 2 handlers — each panel is fully independent
  const handleTogglePlay2 = useCallback(() => {
    const video = video2Ref.current;
    if (!video) return;
    // See the note in handleTogglePlay1.
    if (p2WantsPlayRef.current) {
      p2WantsPlayRef.current = false;
      video.pause();
      return;
    }
    p2WantsPlayRef.current = true;

    const segStart = p2SegStartRef.current;
    const segEnd = segmentEndOf(p2SegEndRef.current, video.duration);

    const atEnd = video.currentTime >= segEnd - (isMobileRef.current ? SEGMENT_END_EPSILON_PHONE : 0);
    const seekTo = video.currentTime <= segStart || atEnd ? segStart : null;

    applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
    syncPairMutes(pairMuteRefs.current, 2);
    startPanelPlayback(video, p2WantsPlayRef, seekTo, (err) => {
      // See the note in handleTogglePlay1.
      const name = (err as { name?: string } | null)?.name;
      if (name === 'AbortError' && isMobileRef.current) return;
      p2WantsPlayRef.current = false;
      setP2Ui('paused');
      debugNote(`panel 2 play() refused: ${name ?? err}`);
    });
  }, [startPanelPlayback]);

  /* Scrub-to-pause.
     Bespoke rather than the shared useScrubPause hook, and for one reason:
     these panels carry an explicit intent ref, and the hook only knows about
     the element. Pausing here fires the `pause` listener, which clears
     pWantsPlayRef — correctly, since reality won. On release we have to put
     the intent back as well as restart the element, or the panel would be
     playing while its ref said otherwise and the next click would call play()
     on a playing video instead of pausing it.

     So intent, not `!video.paused`, is what gets captured: it is the same
     question ("did the coach want this running?") asked of the right source. */
  const p1ScrubResumeRef = useRef(false);
  const p2ScrubResumeRef = useRef(false);

  const handleScrubStart1 = useCallback(() => {
    const video = video1Ref.current;
    if (!video) return;
    p1ScrubResumeRef.current = p1WantsPlayRef.current;
    if (p1WantsPlayRef.current) video.pause();
  }, []);

  const handleScrubEnd1 = useCallback(() => {
    if (!p1ScrubResumeRef.current) return;
    p1ScrubResumeRef.current = false;
    const video = video1Ref.current;
    if (!video) return;
    p1WantsPlayRef.current = true;
    applyVideoState(video, p1SpeedRef.current, p1MutedRef.current);
    syncPairMutes(pairMuteRefs.current);
    // The scrub's last seek may still be in flight; on a phone, wait for it.
    startPanelPlayback(video, p1WantsPlayRef, null, () => { p1WantsPlayRef.current = false; });
  }, [startPanelPlayback]);

  const handleScrubStart2 = useCallback(() => {
    const video = video2Ref.current;
    if (!video) return;
    p2ScrubResumeRef.current = p2WantsPlayRef.current;
    if (p2WantsPlayRef.current) video.pause();
  }, []);

  const handleScrubEnd2 = useCallback(() => {
    if (!p2ScrubResumeRef.current) return;
    p2ScrubResumeRef.current = false;
    const video = video2Ref.current;
    if (!video) return;
    p2WantsPlayRef.current = true;
    applyVideoState(video, p2SpeedRef.current, p2MutedRef.current);
    syncPairMutes(pairMuteRefs.current);
    startPanelPlayback(video, p2WantsPlayRef, null, () => { p2WantsPlayRef.current = false; });
  }, [startPanelPlayback]);

  /* Spec 15.17: side by side scrubbing on a phone. While the finger is on
     the scrubber the element follows it directly, once a frame, and this
     panel's timeupdate write-back and range checks stand down; on release
     the element is put exactly where the finger left it and the panel's
     time is set once. Single video scrubbing keeps handleSeek1. */
  const makePhoneScrub = useCallback((
    videoRef: React.RefObject<HTMLVideoElement | null>,
    scrubbingRef: React.MutableRefObject<boolean>,
    pendingRef: React.MutableRefObject<number | null>,
    timeRef: React.MutableRefObject<number>,
    setTime: (t: number) => void,
    start: () => void,
    end: () => void,
  ) => {
    let raf = 0;
    let target = 0;
    return {
      begin: () => {
        scrubbingRef.current = true;
        pendingRef.current = null;
        start();
      },
      move: (t: number) => {
        target = t;
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const v = videoRef.current;
          if (v) v.currentTime = target;
        });
      },
      end: (t: number) => {
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        const v = videoRef.current;
        if (v) v.currentTime = t;
        timeRef.current = t;
        setTime(t);
        scrubbingRef.current = false;
        end();
      },
    };
  }, []);
  const phoneScrub1 = useMemo(
    () => makePhoneScrub(video1Ref, p1ScrubbingRef, p1PendingSeekRef, p1CurrentTimeRef, setP1CurrentTime, handleScrubStart1, handleScrubEnd1),
    [makePhoneScrub, handleScrubStart1, handleScrubEnd1],
  );
  const phoneScrub2 = useMemo(
    () => makePhoneScrub(video2Ref, p2ScrubbingRef, p2PendingSeekRef, p2CurrentTimeRef, setP2CurrentTime, handleScrubStart2, handleScrubEnd2),
    [makePhoneScrub, handleScrubStart2, handleScrubEnd2],
  );

  const handleSeek2 = useCallback((t: number) => {
    p2CurrentTimeRef.current = t;
    setP2CurrentTime(t);
    const video = video2Ref.current;
    if (!video) return;
    if (video.seeking) {
      p2PendingSeekRef.current = t;
    } else {
      video.currentTime = t;
    }
  }, []);

  const handleSpeedChange2 = useCallback((s: Speed) => {
    p2SpeedRef.current = s;
    if (video2Ref.current) applyVideoState(video2Ref.current, s, p2MutedRef.current);
    syncPairMutes(pairMuteRefs.current);
    setP2Speed(s);
  }, []);

  const handleToggleMute2 = useCallback(() => {
    const next = !p2MutedRef.current;
    p2MutedRef.current = next;
    setP2Muted(next);
    if (video2Ref.current) applyVideoState(video2Ref.current, p2SpeedRef.current, next);
    syncPairMutes(pairMuteRefs.current);
  }, []);

  // Start recording
  const handleStartRecording = useCallback(async () => {
    // Guard on both isRecording state AND the ref — mediaRecorderRef stays non-null
    // until onstop fires, so this prevents starting a new recording while the previous
    // clip is still being finalized (which would clear chunksRef and corrupt the old blob).
    if (isRecording || mediaRecorderRef.current !== null) return;
    canceledRef.current = false;

    // Create / reset composite canvas
    if (!compositeCanvasRef.current) {
      compositeCanvasRef.current = document.createElement('canvas');
    }
    const composite = compositeCanvasRef.current;
    /**
     * Single source: the canvas takes the source's aspect, so containRect below
     * fills it exactly and no bars are drawn. Side by side keeps the existing
     * fixed 2560x720 for now; laying out two arbitrary aspects is its own
     * decision and is not made here.
     */
    const v1El = video1Ref.current;
    const p1Rot = primarySource.rotation ?? 0;
    const p1Swapped = p1Rot === 90 || p1Rot === 270;
    const srcW = p1Swapped ? (v1El?.videoHeight ?? 0) : (v1El?.videoWidth ?? 0);
    const srcH = p1Swapped ? (v1El?.videoWidth ?? 0) : (v1El?.videoHeight ?? 0);
    // A camera source is already drawn edge to edge, so it keeps the landscape frame.
    const single = primarySource.isCameraSource === true
      ? { w: PANEL_W, h: PANEL_H }
      : compositeSizeFor(srcW, srcH);
    const panelW = hasTwoSources ? PANEL_W : single.w;
    const panelH = hasTwoSources ? PANEL_H : single.h;

    composite.width = hasTwoSources ? PANEL_W * 2 : panelW;
    composite.height = panelH;
    const ctx = composite.getContext('2d')!;

    // Initialize canvas to black before loop starts
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, composite.width, composite.height);

    // captureStream(30) — Chrome's internal 30fps limiter keeps encoder load reasonable.
    // The internal capture timer stops when the track is stopped (done synchronously in
    // handleStopRecording via canvasStreamRef), so there is no lingering timer after recording.
    const canvasStream = composite.captureStream(30);
    canvasStreamRef.current = canvasStream;

    // rAF draw loop — for camera sources, video1Ref has srcObject (live feed) so drawing
    // is identical to a normal video source. Skip seeking check for camera (always ready).
    // Compute object-contain draw rect so portrait videos get letterboxed
    // rather than stretched to fill the panel.
    const containRect = (vw: number, vh: number, panelX: number) => {
      const scale = Math.min(panelW / vw, panelH / vh);
      const w = vw * scale;
      const h = vh * scale;
      return { x: panelX + (panelW - w) / 2, y: (panelH - h) / 2, w, h };
    };

    const p1IsCamera = primarySource.isCameraSource === true;
    const p1Rotation = primarySource.rotation ?? 0;
    /* Phone pinch zoom (spec 9.7): draw a panel's video and drawings with the
       zoom and pan the coach sees, clipped to the picture. The on-screen
       stage is exactly the picture, so the pan scales by the picture's size
       here over the stage's size on screen. Nothing else (bubble, timecode)
       is zoomed. At 1x this is a plain draw. */
    const withPanelZoom = (z: ZoomState, rect: { x: number; y: number; w: number; h: number }, draw: () => void) => {
      if (z.s <= 1.001 || z.w <= 0 || z.h <= 0) { draw(); return; }
      ctx.save();
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, rect.w, rect.h);
      ctx.clip();
      ctx.translate(rect.x + (z.tx * rect.w) / z.w, rect.y + (z.ty * rect.h) / z.h);
      ctx.scale(z.s, z.s);
      ctx.translate(-rect.x, -rect.y);
      draw();
      ctx.restore();
    };

    const drawFrame = () => {
      const v1 = video1Ref.current;
      if (v1 && (p1IsCamera || !v1.seeking)) {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, panelW, panelH);
        const z1Rect = (() => {
          if (p1IsCamera) return { x: 0, y: 0, w: panelW, h: panelH };
          const vw0 = v1.videoWidth || panelW;
          const vh0 = v1.videoHeight || panelH;
          const isSwapped = p1Rotation === 90 || p1Rotation === 270;
          return containRect(isSwapped ? vh0 : vw0, isSwapped ? vw0 : vh0, 0);
        })();
        withPanelZoom(panel1ZoomRef.current, z1Rect, () => {
          if (v1.readyState >= 2) {
            if (p1IsCamera) {
              ctx.save();
              ctx.scale(-1, 1);
              ctx.drawImage(v1, -panelW, 0, panelW, panelH);
              ctx.restore();
            } else {
              const vw0 = v1.videoWidth || panelW;
              const vh0 = v1.videoHeight || panelH;
              // For 90/270 rotation the visual aspect ratio flips — swap dims for containRect
              const isSwapped = p1Rotation === 90 || p1Rotation === 270;
              const r = containRect(isSwapped ? vh0 : vw0, isSwapped ? vw0 : vh0, 0);
              if (p1Rotation !== 0) {
                ctx.save();
                ctx.translate(r.x + r.w / 2, r.y + r.h / 2);
                ctx.rotate((p1Rotation * Math.PI) / 180);
                // In rotated space, natural video dimensions fill a rect of size r.h × r.w
                ctx.drawImage(v1, -r.h / 2, -r.w / 2, r.h, r.w);
                ctx.restore();
              } else {
                ctx.drawImage(v1, r.x, r.y, r.w, r.h);
              }
            }
          }
          const ac1 = annotationCanvas1Ref.current;
          if (ac1) {
            if (p1IsCamera) {
              ctx.drawImage(ac1, 0, 0, panelW, panelH);
            } else {
              const vw0 = v1.videoWidth || panelW;
              const vh0 = v1.videoHeight || panelH;
              const isSwapped = p1Rotation === 90 || p1Rotation === 270;
              const ar = containRect(isSwapped ? vh0 : vw0, isSwapped ? vw0 : vh0, 0);
              ctx.drawImage(ac1, ar.x, ar.y, ar.w, ar.h);
            }
          }
        });
      }

      if (hasTwoSources) {
        const v2 = video2Ref.current;
        const p2IsCamera = secondarySource?.isCameraSource === true;
        if (v2 && (p2IsCamera || !v2.seeking)) {
          ctx.fillStyle = '#000';
          ctx.fillRect(PANEL_W, 0, PANEL_W, PANEL_H);
          const z2Rect = (() => {
            if (p2IsCamera) return { x: PANEL_W, y: 0, w: PANEL_W, h: PANEL_H };
            return containRect(v2.videoWidth || PANEL_W, v2.videoHeight || PANEL_H, PANEL_W);
          })();
          withPanelZoom(panel2ZoomRef.current, z2Rect, () => {
            if (v2.readyState >= 2) {
              const vw = v2.videoWidth || PANEL_W;
              const vh = v2.videoHeight || PANEL_H;
              const r = containRect(vw, vh, PANEL_W);
              ctx.drawImage(v2, r.x, r.y, r.w, r.h);
            }
            const ac2 = annotationCanvas2Ref.current;
            if (ac2) {
              if (p2IsCamera) {
                ctx.drawImage(ac2, PANEL_W, 0, PANEL_W, PANEL_H);
              } else {
                const vw = v2.videoWidth || PANEL_W;
                const vh = v2.videoHeight || PANEL_H;
                const ar2 = containRect(vw, vh, PANEL_W);
                ctx.drawImage(ac2, ar2.x, ar2.y, ar2.w, ar2.h);
              }
            }
          });
        }
      }

      // Draw timestamp watermarks — only for non-camera panels
      if (!p1IsCamera) {
        const v1Now = video1Ref.current;
        if (v1Now && !v1Now.seeking && v1Now.readyState >= 2) {
          ctx.save();
          const FONT_SIZE = 28;
          ctx.font = `bold ${FONT_SIZE}px monospace`;
          const text = formatTimeDetailed(p1CurrentTimeRef.current);
          const textW = ctx.measureText(text).width;
          const pad = 8, bx = 14, by = 14;
          ctx.fillStyle = 'rgba(0,0,0,0.65)';
          ctx.fillRect(bx, by, textW + pad * 2, FONT_SIZE + pad * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fillText(text, bx + pad, by + pad + FONT_SIZE);
          ctx.restore();
        }
      }
      if (hasTwoSources && !secondarySource?.isCameraSource) {
        const v2Now = video2Ref.current;
        if (v2Now && !v2Now.seeking && v2Now.readyState >= 2) {
          ctx.save();
          const FONT_SIZE = 28;
          ctx.font = `bold ${FONT_SIZE}px monospace`;
          const text = formatTimeDetailed(p2CurrentTimeRef.current);
          const textW = ctx.measureText(text).width;
          const pad = 8, bx = PANEL_W + 14, by = 14;
          ctx.fillStyle = 'rgba(0,0,0,0.65)';
          ctx.fillRect(bx, by, textW + pad * 2, FONT_SIZE + pad * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fillText(text, bx + pad, by + pad + FONT_SIZE);
          ctx.restore();
        }
      }

      // Draw PiP camera overlay — circular clip of camera feed composited on top.
      // pip.x is normalized 0–1 across the full panels container, which maps to the
      // full composite canvas width (PANEL_W for single source, PANEL_W*2 for two sources).
      if (pipEnabledRef.current) {
        const pipVid = pipCaptureVideoRef.current;
        if (pipVid && pipVid.readyState >= 2) {
          /* The same clamp the drag applies, so the two cannot diverge even if
             a position arrives from before the box was measured. */
          /**
           * 10% of the container's width on screen (the element is
           * `width: 10%` with aspect-ratio 1), so 5% of it as a radius here.
           * It used to be a flat 110px, which on a single 1280 panel recorded
           * a bubble about 72% larger than the one the coach was framing, and
           * on a 2560 side-by-side canvas recorded a smaller one.
           */
          /* One panel, not the pair: side by side the composite is two 1280
             halves, so the bubble is sized off a half and lands the same
             relative size as it does in single source and on screen. */
          const pipPanelW = hasTwoSources ? composite.width / 2 : composite.width;
          let PIP_R: number;
          let cx: number;
          let cy: number;
          if (isMobileRef.current) {
            /* Phone (spec 13.10): the same normalized centre and size as on
               screen, against the same picture: that panel's video as it
               sits in the composite (the whole frame for one video). */
            const ratio = phonePipRatioRef.current;
            let rect = { x: 0, y: 0, w: composite.width, h: panelH };
            if (hasTwoSources) {
              const second = ratio.panel === 1;
              const v = second ? video2Ref.current : video1Ref.current;
              const cam = second ? secondarySource?.isCameraSource === true : p1IsCamera;
              const ox = second ? PANEL_W : 0;
              if (cam || !v || !v.videoWidth || !v.videoHeight) rect = { x: ox, y: 0, w: PANEL_W, h: PANEL_H };
              else {
                const swapped = !second && (p1Rotation === 90 || p1Rotation === 270);
                rect = containRect(swapped ? v.videoHeight : v.videoWidth, swapped ? v.videoWidth : v.videoHeight, ox);
              }
            }
            PIP_R = (ratio.d * rect.w) / 2;
            cx = rect.x + ratio.x * rect.w;
            cy = rect.y + ratio.y * rect.h;
          } else {
            PIP_R = pipDiameterFor(pipPanelW, composite.height, hasTwoSources, false) / 2;
            const pip = clampPip(
              pipPosRef.current.x,
              pipPosRef.current.y,
              composite.width,
              panelH,
              PIP_R * 2,
            );
            cx = pip.x * composite.width;
            cy = pip.y * panelH;
          }
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, PIP_R, 0, Math.PI * 2);
          ctx.clip();
          // Mirror camera feed horizontally
          ctx.translate(cx * 2, 0);
          ctx.scale(-1, 1);
          // Cover-crop the video to fill the circle without distorting aspect ratio
          const vw = pipVid.videoWidth || 1;
          const vh = pipVid.videoHeight || 1;
          const diameter = PIP_R * 2;
          const scale = Math.max(diameter / vw, diameter / vh);
          const srcW = diameter / scale;
          const srcH = diameter / scale;
          const srcX = (vw - srcW) / 2;
          const srcY = (vh - srcH) / 2;
          ctx.drawImage(pipVid, srcX, srcY, srcW, srcH, cx - PIP_R, cy - PIP_R, diameter, diameter);
          ctx.restore();
          // White border ring
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.85)';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(cx, cy, PIP_R, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
      }

      rafIdRef.current = requestAnimationFrame(drawFrame);
    };

    try {
      // For camera primary source, reuse the camera stream's built-in audio.
      // For other sources, request a separate microphone stream.
      let audioSourceStream: MediaStream;
      if (isMobileRef.current) {
        /* Phone (spec 13.6): the session's microphone, asked for with the
           camera the first time and kept; re-asked once if iOS ended it.
           Not in micStreamRef, so stopping a take never stops it. */
        const session = await keepPlayingThrough(acquireCapture);
        audioSourceStream = new MediaStream(session.getAudioTracks());
      } else if (p1IsCamera && cameraStreamRef.current?.getAudioTracks().length) {
        audioSourceStream = cameraStreamRef.current;
        // Don't assign to micStreamRef — camera stream cleanup is managed by its own useEffect
      } else {
        audioSourceStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        micStreamRef.current = audioSourceStream;
      }

      // Mix audio source with any unmuted video panel audio via Web Audio API
      captureStreamsRef.current = [];
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      const mixedDest = audioCtx.createMediaStreamDestination();
      if (audioSourceStream.getAudioTracks().length) {
        audioCtx.createMediaStreamSource(audioSourceStream).connect(mixedDest);
      }
      if (!p1MutedRef.current && !primarySource.isCameraSource && video1Ref.current) {
        const vs = (video1Ref.current as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream?.();
        if (vs) {
          captureStreamsRef.current.push(vs);
          if (vs.getAudioTracks().length) audioCtx.createMediaStreamSource(vs).connect(mixedDest);
        }
      }
      if (!p2MutedRef.current && secondarySource && !secondarySource.isCameraSource && video2Ref.current) {
        const vs = (video2Ref.current as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream?.();
        if (vs) {
          captureStreamsRef.current.push(vs);
          if (vs.getAudioTracks().length) audioCtx.createMediaStreamSource(vs).connect(mixedDest);
        }
      }

      const combined = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...mixedDest.stream.getAudioTracks(),
      ]);

      // Prefer VP9 (better quality/bitrate ratio) over VP8, fall back to plain webm
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm';

      const recorder = new MediaRecorder(combined, {
        mimeType,
        videoBitsPerSecond: 8_000_000,  // 8 Mbps for crisp 720p composite
        audioBitsPerSecond: 128_000,
      });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      /* Spec 15.27: one finaliser, run once, by the recorder's stop event or
         by the watchdog handleStopRecording arms on a phone if that event
         never comes. */
      let finalised = false;
      const finalise = () => {
        if (finalised) return;
        finalised = true;
        // Stop canvas/combined streams (audio resources released in handleStopRecording)
        combined.getTracks().forEach((t) => t.stop());
        canvasStream.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current = null;
        stopWatchdogRef.current = null;

        if (canceledRef.current) {
          const wasAbort = abortRef.current;
          canceledRef.current = false;
          abortRef.current = false;
          if (wasAbort) setIsRecording(false);
          return;
        }

        // Capture thumbnail directly from the composite canvas — synchronous, zero CPU cost.
        // Do NOT load the recording blob into a <video> element: MediaRecorder WebM blobs
        // have duration=Infinity in the header, so Chrome scans the entire blob to resolve
        // the real duration before firing loadedmetadata. For large recordings this causes
        // several seconds of CPU spin immediately after "Stop & Save" is clicked.
        // The composite canvas already holds the last recorded frame, so we draw it scaled
        // down to 160×90 and export as JPEG.
        // Capture thumbnail from composite canvas — works for all source types including
        // camera (video1Ref has srcObject, its last frame is already on the composite canvas).
        let thumbnailDataUrl: string | undefined;
        if (compositeCanvasRef.current) {
          const c = document.createElement('canvas');
          c.width = 160; c.height = 90;
          const ctx2 = c.getContext('2d');
          const src = compositeCanvasRef.current;
          if (ctx2) ctx2.drawImage(src, 0, 0, src.width, src.height, 0, 0, 160, 90);
          thumbnailDataUrl = c.toDataURL('image/jpeg', 0.7);
        }

        const videoBlob = new Blob(chunksRef.current, {
          type: mimeType.split(';')[0],
        });
        const segmentIds = [
          primarySource.segment?.id,
          secondarySource?.segment?.id,
        ].filter(Boolean) as string[];

        const clip: VoiceoverNotableSegmentClip = {
          id: `voiceover-clip-${Date.now()}`,
          insertionPoint: 0, // deprecated — sequence is determined by array position
          isCameraSource: primarySource.isCameraSource,
          segmentIds,
          videoBlob,
          thumbnailDataUrl,
          // Wall-clock duration avoids the expensive blob-scan in getVideoBlobDuration
          videoDuration: (Date.now() - recordingStartMsRef.current) / 1000,
        };

        const end: TakeEnd = saveModeRef.current === 'continue' ? 'save'
          : saveModeRef.current === 'close' ? 'save-and-leave' : 'unplanned';
        const outcome = takeOutcome(end, isMobileRef.current);
        if (outcome.handOver === 'stay') {
          onSaveAndContinue(clip);
          setIsRecording(false);
          /* Phone (spec 15.5): the videos stay exactly where the take
             ended, paused; no seek back to the segment's start. The bubble
             and the drawing tool are left as they were too. */
          if (isMobileRef.current) {
            video1Ref.current?.pause();
            video2Ref.current?.pause();
          }
        } else {
          onSave(clip);
          if (outcome.closeRecorder) onClose();
        }
      };
      recorder.onstop = finalise;
      recorder.onerror = (e) => {
        debugNote(`recorder error: ${String((e as { error?: { name?: string } }).error?.name ?? 'unknown')}`);
        finalise();
      };
      finaliseRef.current = finalise;

      recordingStartMsRef.current = Date.now();
      /* Phone (spec 13.7): until the coach saves or leaves, an ending take is unplanned. */
      if (isMobileRef.current) saveModeRef.current = 'unplanned';
      recorder.start(100);
      mediaRecorderRef.current = recorder;
      rafIdRef.current = requestAnimationFrame(drawFrame);
      setRecordingError(null);
      setIsRecording(true);
    } catch (err) {
      console.error('ClipEditorModal: failed to start recording', err);
      // Stop the canvas stream that was started before the try block
      canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
      canvasStreamRef.current = null;
      const name = (err instanceof DOMException) ? err.name : '';
      let msg: string;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Microphone access was blocked. Open your browser’s site settings, allow the microphone for this site, then try again.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        msg = 'No microphone found. Connect a microphone and try again.';
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        msg = 'Your microphone is in use by another app. Close it and try again.';
      } else if (name === 'OverconstrainedError') {
        msg = 'No microphone matched the required settings. Try a different device.';
      } else if (name === 'SecurityError') {
        msg = 'Recording requires a secure connection (HTTPS). Contact support if this persists.';
      } else {
        msg = 'Recording could not start. Check your microphone and try again.';
      }
      setRecordingError(msg);
    }
  }, [
    isRecording,
    hasTwoSources,
    primarySource,
    secondarySource,
    onSave,
    onSaveAndContinue,
    onClose,
    keepPlayingThrough,
  ]);

  // Stop & save recording (shared by "Save & Close" and "Save & Continue")
  const handleStopRecording = useCallback((mode: 'close' | 'continue' = 'close') => {
    if (!isRecording) return;
    saveModeRef.current = mode;
    canceledRef.current = false;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Pause source videos immediately so they stop decoding while onstop/thumbnail runs
    video1Ref.current?.pause();
    video2Ref.current?.pause();
    if (isMobileRef.current) {
      /* Phone (spec 15.27): stop the recorder first, then its tracks, the
         order the spec describes; and if its stop event has not come after
         STOP_WATCHDOG_MS, finalise the take from the chunks received. */
      if (mediaRecorderRef.current?.state !== 'inactive') {
        try { mediaRecorderRef.current?.requestData(); } catch { /* not recording */ }
        mediaRecorderRef.current?.stop();
      }
      canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
      canvasStreamRef.current = null;
      if (stopWatchdogRef.current) clearTimeout(stopWatchdogRef.current);
      stopWatchdogRef.current = setTimeout(() => {
        stopWatchdogRef.current = null;
        if (mediaRecorderRef.current !== null) {
          debugNote('recorder stop event never came: finalising from the chunks');
          finaliseRef.current?.();
        }
      }, STOP_WATCHDOG_MS);
    } else {
      // Stop canvas stream tracks BEFORE recorder.stop() — this signals to Chrome's
      // MediaRecorder encoder that the video input has definitively ended, so it can
      // finalize the file without spin-waiting for the next frame (which would never
      // arrive since the RAF loop is already cancelled above).
      canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
      canvasStreamRef.current = null;
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef.current?.stop();
      }
    }
    // Release audio resources synchronously — don't wait for recorder.onstop.
    // getUserMedia echo-cancellation monitors the tab's audio output on a background
    // thread; if we defer cleanup to onstop (async), the monitor keeps running and
    // causes visible CPU spin from the moment "Stop & Save" is clicked.
    captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    captureStreamsRef.current = [];
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    // Stop PiP camera — not the primary/secondary camera streams (managed by their own useEffects).
    // In 'continue' mode the user is immediately recording again, so leave the PiP stream and
    // toggle state intact so the overlay is ready without any re-acquisition delay.
    if (mode !== 'continue') {
      pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      pipCameraStreamRef.current = null;
      pipCaptureVideoRef.current = null;
      pipEnabledRef.current = false;
      setPipEnabled(false);
    }
    // Note: setIsRecording(false) is called inside recorder.onstop for 'continue' mode,
    // and implicitly via onClose for 'close' mode.
  }, [isRecording]);

  /** Stop the current recording without saving it; keep the modal open. */
  const handleAbortRecording = useCallback(() => {
    if (!isRecording) return;
    canceledRef.current = true;
    abortRef.current = true;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    video1Ref.current?.pause();
    video2Ref.current?.pause();
    canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
    canvasStreamRef.current = null;
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    captureStreamsRef.current = [];
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    // pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    // pipCameraStreamRef.current = null;
    // pipCaptureVideoRef.current = null;
    // pipEnabledRef.current = false;
    // setPipEnabled(false);
    // setIsRecording(false) is called in recorder.onstop when abortRef is true
  }, [isRecording]);

  // Cleanup on unmount — stop all streams and cancel any active recording
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
      canvasStreamRef.current = null;
      if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
      captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
      captureStreamsRef.current = [];
      micStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioContextRef.current?.close();
      cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      secondaryCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
      video1Ref.current?.pause();
      video2Ref.current?.pause();
    };
  }, []);

  /* Elapsed recording time. On-screen only — it is never drawn to the
     composite canvas, so it cannot reach the recorded file.
     A ref for the start instant and state for the display, ticking once a
     second: the coach needs to know a recording is long, not to time it to
     the frame. */
  const recordingStartedAtRef = useRef<number | null>(null);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  useEffect(() => {
    if (!isRecording) {
      recordingStartedAtRef.current = null;
      setRecordingElapsed(0);
      return;
    }
    recordingStartedAtRef.current = Date.now();
    setRecordingElapsed(0);
    const id = setInterval(() => {
      const started = recordingStartedAtRef.current;
      if (started !== null) setRecordingElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  /* Phone pinch zoom per panel (spec 9.7), read by the composite draw loop. */
  const panel1ZoomRef = useRef<ZoomState>({ ...IDENTITY_ZOOM });
  const panel2ZoomRef = useRef<ZoomState>({ ...IDENTITY_ZOOM });

  /* Phone: each panel's video aspect, keyed by panel and source, for the
     stacked pair's height split (stackedPanelBounds). */
  const [panelAspects, setPanelAspects] = useState<Record<string, number>>({});
  const notePanelAspect = useCallback((key: string, a: number) => {
    setPanelAspects((prev) => (prev[key] !== undefined && Math.abs(prev[key] - a) < 0.001 ? prev : { ...prev, [key]: a }));
  }, []);

  /* Bottom right by default; the clamp's own inset puts it 16px off both
     edges, so {1, 1} resolves to exactly that. Dragging moves it from there. */
  const [pipPos, setPipPos] = useState({ x: 1, y: 1 });
  const pipPosRef = useRef(pipPos);
  const pipPosDraggingRef = useRef(false);

  /**
   * A pair on a phone stacks, primary on top. The recording is still side by
   * side and still reads pipPos as before: x across both halves, y down one
   * panel. Only the on-screen drawing changes. x below one half is drawn on
   * the top picture, the rest on the bottom one, so the bubble sits where it
   * will record. Each rect is that panel's picture, relative to the container.
   */
  const stacked = isMobile && hasTwoSources;
  const [stackRects, setStackRects] = useState<{ a: DOMRectLike; b: DOMRectLike } | null>(null);
  /** The box positions are measured against: the pair as the composite sees it. */
  const pairBox = stacked && stackRects
    ? { width: stackRects.a.width * 2, height: stackRects.a.height }
    : videoBox;

  /* One panel's width: half the pair when there are two, the whole box when
     there is one. The composite applies the identical rule. */
  const pipPanelWidth = (pairBox?.width ?? 0) / (hasTwoSources ? 2 : 1);
  /* Paired: videoBox is the whole pair, so the panel is half its width and all
     of its height. Single: videoBox is already the contained picture. */
  const pipDiameter = pipDiameterFor(pipPanelWidth, pairBox?.height ?? 0, hasTwoSources, isMobile);

  /* Phone bubble (spec 13.10): a centre normalized against one panel's video
     as displayed, drawn inside that video (which clips it) and clamped
     against its current size every render, so drag, resize, rotation,
     metadata and recovery can never put it outside. A size from that
     panel's width (10.7). Refs carry what the composite draw loop needs. */
  /* Spec 15.1: landscape, one video: the video at the full height, the
     close X, the tool columns and the timeline pill. */
  const recSingle = isMobile && phoneV.landscape && !hasTwoSources
    ? landscapeSingleLayout(phoneV, panelAspects[`1:${primarySource.videoUrl}`] ?? DEFAULT_ASPECT)
    : null;
  /* Spec 15.2: landscape, side by side: the box each video fits, above its
     title and row and the bottom bar. */
  const recPair = isMobile && phoneV.landscape && hasTwoSources ? landscapePairLayout(phoneV) : null;

  /* Spec 15.14: both landscape columns collapsed to their chevrons, or not,
     kept for the session. */
  const [columnsCollapsed, setColumnsCollapsed] = useState(readColumnsCollapsed);
  const toggleColumns = useCallback(() => {
    const next = !columnsCollapsed;
    rememberColumnsCollapsed(next);
    setColumnsCollapsed(next);
  }, [columnsCollapsed]);

  /* Spec 15.9: the landscape timeline pill fades out 3 s after playback or a
     take starts. It comes back when either stops (a change in either brings
     it back, and it fades again while one is still running), while
     scrubbing, and on a tap in the video's bottom 40. */
  const timelineActive = p1IsPlaying || isRecording;
  const timelineKey = `${p1IsPlaying}|${isRecording}`;
  const [timelineSeen, setTimelineSeen] = useState(timelineKey);
  const [timelineHidden, setTimelineHidden] = useState(false);
  const [timelinePoke, setTimelinePoke] = useState(0);
  const [timelineScrubbing, setTimelineScrubbing] = useState(false);
  if (timelineSeen !== timelineKey) {
    setTimelineSeen(timelineKey);
    setTimelineHidden(false);
  }
  useEffect(() => {
    if (!timelineActive || timelineHidden || timelineScrubbing) return;
    const t = setTimeout(() => setTimelineHidden(true), 3000);
    return () => clearTimeout(t);
  }, [timelineActive, timelineHidden, timelineScrubbing, timelinePoke]);
  const timelineShown = !timelineHidden || timelineScrubbing;
  /* Spec 15.16: a tap on the video shows or hides the pill at once. Shown
     while something runs, it fades again 3 s later. */
  const toggleTimeline = useCallback(() => {
    setTimelineHidden((h) => !h);
    setTimelinePoke((n) => n + 1);
  }, []);

  const [bubble, setBubble] = useState<BubblePos>(readBubble);
  const [bubbleDrag, setBubbleDrag] = useState<BubblePos | null>(null);
  /** Each panel's picture, relative to the panels container (data-phone-stage). */
  const [phoneStageRects, setPhoneStageRects] = useState<{ 1?: DOMRectLike; 2?: DOMRectLike }>({});
  const bubbleShown = bubbleDrag ?? bubble;
  const bubblePanel: 1 | 2 = hasTwoSources ? bubbleShown.panel : 1;
  const bubbleRect = phoneStageRects[bubblePanel] ?? phoneStageRects[1] ?? null;
  const phonePipD = isMobile && bubbleRect ? phonePipDiameter(bubbleRect.width, hasTwoSources) : 0;
  /* Spec 15.11: 8 from every edge of the video itself. Nothing over the
     video (the timeline pill, a column) shortens its reach: those are not
     in the recording, and the pill hides itself during a take (15.9). */
  const bubbleInsets: BubbleInsets = PHONE_BUBBLE_INSETS;
  const bubbleAt = bubbleRect
    ? clampBubble(bubbleShown.x, bubbleShown.y, bubbleRect.width, bubbleRect.height, phonePipD, bubbleInsets)
    : { x: 0.5, y: 0.5 };
  const phonePipRatioRef = useRef({ d: 0.18, x: 0.85, y: 0.8, panel: 0 });
  phonePipRatioRef.current = bubbleRect && bubbleRect.width > 0
    ? { d: phonePipD / bubbleRect.width, x: bubbleAt.x, y: bubbleAt.y, panel: bubblePanel - 1 }
    : { d: 0.18, x: 0.85, y: 0.8, panel: 0 };

  /* Re-clamp on resize: a fraction that was legal in one box can put the
     bubble half outside a smaller one. */
  useEffect(() => {
    if (!pairBox || pairBox.width <= 0 || pairBox.height <= 0) return;
    const next = clampPip(
      pipPosRef.current.x, pipPosRef.current.y,
      pairBox.width, pairBox.height, pipDiameter,
    );
    if (next.x !== pipPosRef.current.x || next.y !== pipPosRef.current.y) {
      pipPosRef.current = next;
      setPipPos(next);
    }
  // pairBox is derived from these two each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoBox, stackRects, pipDiameter]);

  /** Where pipPos is drawn, relative to the panels container. */
  const pipScreenFor = (p: { x: number; y: number }) => {
    if (stacked && stackRects) {
      const second = p.x >= 0.5;
      const r = second ? stackRects.b : stackRects.a;
      const lx = (p.x - (second ? 0.5 : 0)) * 2;
      return { left: r.left + lx * r.width, top: r.top + p.y * r.height };
    }
    return {
      left: (videoBox?.left ?? 0) + p.x * (videoBox?.width ?? 0),
      top: (videoBox?.top ?? 0) + p.y * (videoBox?.height ?? 0),
    };
  };
  const pipScreen = pipScreenFor(pipPos);
  /**
   * A touch drag's anchor: where the finger came down and where the bubble
   * was, both in the container. The bubble then moves by how far the finger
   * has moved, so it does not jump to put its centre under the fingertip.
   * Null for a mouse, which keeps following the pointer as it always has.
   */
  const pipTouchAnchorRef = useRef<{ px: number; py: number; left: number; top: number } | null>(null);

  /** The inverse: a point in the container, as a pipPos before clamping. */
  const pipFromPoint = useCallback((px: number, py: number): { x: number; y: number } | null => {
    if (stacked && stackRects) {
      const { a, b } = stackRects;
      /* Stacked in portrait, side by side in landscape: split on whichever
         axis the two pictures are laid out along. */
      const sideways = b.left >= a.left + a.width - 1;
      const second = sideways ? px > (a.left + a.width + b.left) / 2 : py > (a.top + a.height + b.top) / 2;
      const r = second ? b : a;
      if (r.width <= 0 || r.height <= 0) return null;
      const lx = Math.max(0, Math.min(1, (px - r.left) / r.width));
      return { x: (second ? 0.5 : 0) + lx * 0.5, y: (py - r.top) / r.height };
    }
    if (!videoBox || videoBox.width <= 0 || videoBox.height <= 0) return null;
    return { x: (px - videoBox.left) / videoBox.width, y: (py - videoBox.top) / videoBox.height };
  }, [stacked, stackRects, videoBox]);

  /**
   * The bubble by touch, on a phone (spec 2e). Chrome's emulation passed with
   * pointer events and the iPhone did not, so on a phone touch events are
   * the primary path: native listeners with passive: false, because React
   * registers its touch listeners as passive and preventDefault in them does
   * nothing. The bubble moves by exactly the finger's movement from where it
   * came down, through the recording's own clamp. Pointer events from a
   * touch are ignored on a phone so the two paths never both move it.
   */
  const pipDragCtxRef = useRef({ bubbleAt, bubblePanel, phonePipD, hasTwoSources, bubbleInsets });
  useEffect(() => { pipDragCtxRef.current = { bubbleAt, bubblePanel, phonePipD, hasTwoSources, bubbleInsets }; });
  const pipTouchRef = useRef<{ px: number; py: number; x: number; y: number; panel: 1 | 2; w: number; h: number } | null>(null);
  const pipTouchCleanupRef = useRef<(() => void) | null>(null);
  const attachPipTouch = useCallback((el: HTMLDivElement | null) => {
    pipTouchCleanupRef.current?.();
    pipTouchCleanupRef.current = null;
    if (!el) return;
    /* Phone (spec 13.10): the bubble follows the finger by exactly its
       movement, clamped live inside its video, and stays where it is
       dropped. Side by side, dropped over the other video it moves there. */
    const stageRect = (panel: 1 | 2) => panelsContainerRef.current?.querySelector(`[data-phone-stage="${panel}"]`)?.getBoundingClientRect() ?? null;
    const placeFrom = (a: NonNullable<typeof pipTouchRef.current>, t: Touch): BubblePos => ({
      panel: a.panel,
      ...clampBubble(a.x + (t.clientX - a.px) / a.w, a.y + (t.clientY - a.py) / a.h, a.w, a.h, pipDragCtxRef.current.phonePipD, pipDragCtxRef.current.bubbleInsets),
    });
    const onStart = (e: TouchEvent) => {
      if (!isMobileRef.current || e.touches.length !== 1) return;
      e.preventDefault();
      const ctx = pipDragCtxRef.current;
      const r = stageRect(ctx.bubblePanel);
      if (!r || r.width <= 0 || r.height <= 0) return;
      const t = e.touches[0];
      pipTouchRef.current = { px: t.clientX, py: t.clientY, x: ctx.bubbleAt.x, y: ctx.bubbleAt.y, panel: ctx.bubblePanel, w: r.width, h: r.height };
    };
    const onMove = (e: TouchEvent) => {
      const a = pipTouchRef.current;
      if (!a) return;
      e.preventDefault();
      setBubbleDrag(placeFrom(a, e.touches[0]));
    };
    const onEnd = (e: TouchEvent) => {
      const a = pipTouchRef.current;
      pipTouchRef.current = null;
      if (!a) return;
      const t = e.changedTouches[0];
      if (t) {
        let next = placeFrom(a, t);
        if (pipDragCtxRef.current.hasTwoSources) {
          const other: 1 | 2 = a.panel === 1 ? 2 : 1;
          const r = stageRect(other);
          if (r && r.width > 0 && t.clientX >= r.left && t.clientX <= r.right && t.clientY >= r.top && t.clientY <= r.bottom) {
            next = { panel: other, ...clampBubble((t.clientX - r.left) / r.width, (t.clientY - r.top) / r.height, r.width, r.height, phonePipDiameter(r.width, true), PHONE_BUBBLE_INSETS) };
          }
        }
        setBubble(next);
        saveBubble(next);
      }
      setBubbleDrag(null);
    };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    pipTouchCleanupRef.current = () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const measureVideoBox = useCallback(() => {
    const container = panelsContainerRef.current;
    if (!container) return;
    const c = container.getBoundingClientRect();
    /* Phone (spec 10.7): each panel's picture, where the bubble lives. */
    if (isMobileRef.current) {
      const next: { 1?: DOMRectLike; 2?: DOMRectLike } = {};
      container.querySelectorAll<HTMLElement>('[data-phone-stage]').forEach((el) => {
        const r = el.getBoundingClientRect();
        const id = el.dataset.phoneStage === '2' ? 2 : 1;
        next[id] = { left: r.left - c.left, top: r.top - c.top, width: r.width, height: r.height };
      });
      setPhoneStageRects((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    }
    if (hasTwoSources) {
      setVideoBox({ left: 0, top: 0, width: c.width, height: c.height });
      if (stacked) {
        const ra = video1Ref.current?.getBoundingClientRect();
        const rb = video2Ref.current?.getBoundingClientRect();
        if (ra && rb) {
          const rel = (r: DOMRect) => ({ left: r.left - c.left, top: r.top - c.top, width: r.width, height: r.height });
          setStackRects({ a: rel(ra), b: rel(rb) });
        }
      }
      return;
    }
    const v = video1Ref.current;
    if (!v || !v.videoWidth || !v.videoHeight) {
      setVideoBox({ left: 0, top: 0, width: c.width, height: c.height });
      return;
    }
    const e = v.getBoundingClientRect();
    const rot = primarySource.rotation ?? 0;
    const swapped = rot === 90 || rot === 270;
    const vw = swapped ? v.videoHeight : v.videoWidth;
    const vh = swapped ? v.videoWidth : v.videoHeight;
    const scale = Math.min(e.width / vw, e.height / vh);
    const w = vw * scale;
    const h = vh * scale;
    setVideoBox({
      left: e.left - c.left + (e.width - w) / 2,
      top: e.top - c.top + (e.height - h) / 2,
      width: w,
      height: h,
    });
  }, [hasTwoSources, stacked, primarySource.rotation]);

  useEffect(() => {
    measureVideoBox();
    const container = panelsContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(measureVideoBox);
    ro.observe(container);
    /* Stacked, each picture settles its own size as its video reports in,
       which the container does not see. */
    if (stacked) {
      if (video1Ref.current) ro.observe(video1Ref.current);
      if (video2Ref.current) ro.observe(video2Ref.current);
    }
    /* Phone: the pictures settle their own size as their videos report in. */
    if (isMobile) container.querySelectorAll('[data-phone-stage]').forEach((el) => ro.observe(el));
    return () => ro.disconnect();
    // p1Duration changes on loadedmetadata, which is when videoWidth appears.
  }, [measureVideoBox, p1Duration, isOpen, stacked, isMobile, hasTwoSources]);

  // Any position from before the space changed is pulled back into range.

  // Cancel / close
  const handleClose = useCallback(() => {
    canceledRef.current = true;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    canvasStreamRef.current?.getTracks().forEach((t) => t.stop());
    canvasStreamRef.current = null;
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    captureStreamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    captureStreamsRef.current = [];
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    pipCameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    pipCameraStreamRef.current = null;
    pipCaptureVideoRef.current = null;
    video1Ref.current?.pause();
    video2Ref.current?.pause();
    setIsRecording(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  /* Phone (spec 11.3, 13.10): a true circle (an explicit height) inside an
     invisible ring 8 wider all round that always takes the touch, so a finger
     on the bubble's edge moves it and never draws. Drawn inside its panel's
     video, which clips it, at its normalized centre; not drawn until the
     picture has been measured. */
  const phoneBubble = pipEnabled && isMobile && phonePipD > 0 ? (
    <div
      ref={attachPipTouch}
      data-testid="camera-bubble"
      className="absolute rounded-full cursor-move pointer-events-auto"
      style={{
        width: phonePipD + 16,
        height: phonePipD + 16,
        left: `${bubbleAt.x * 100}%`,
        top: `${bubbleAt.y * 100}%`,
        transform: 'translate(-50%, -50%)',
        touchAction: 'none',
        zIndex: 30,
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      <div className="absolute rounded-full border-2 border-[var(--editor-border)] shadow-lg overflow-hidden" style={{ inset: 8 }}>
        <video ref={setPipVideoRef} autoPlay muted playsInline {...PHONE_INLINE_EXTRAS} className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)', pointerEvents: 'none' }} />
      </div>
    </div>
  ) : null;

  /* The desktop bubble. */
  const pipBubble = (
    <>
          {/* PiP camera bubble — floats over the full panels area so it can be dragged across both videos */}
          {pipEnabled && !isMobile && (
            <div
              ref={attachPipTouch}
              className="absolute rounded-full border-2 border-[var(--editor-border)] shadow-lg overflow-hidden cursor-move z-20 pointer-events-auto"
              /* Sized off one panel, not the box it floats over — in side by
                 side that box is the pair, which is what made the paired bubble
                 twice the single-source size. Position is the coach's, run
                 through the same clamp the composite uses. */
              style={{
                /* Phone: a corner of one panel's picture, or under the
                   finger while it is dragged (spec 10.7). Not drawn until
                   the picture has been measured, so it never shows outside
                   it. */
                width: pipDiameter,
                aspectRatio: '1',
                left: pipScreen.left,
                top: pipScreen.top,
                transform: 'translate(-50%, -50%)',
                /* Without this a finger's drag is taken as a scroll: the browser
                   cancels the pointer a few pixels in, which is why the bubble
                   barely moved on a phone. */
                touchAction: 'none',
                /* Phone: above every canvas and overlay in the stage, never
                   selectable, no long-press callout. */
                ...(isMobile ? { zIndex: 30, WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' } : {}),
              }}
              onPointerDown={(e) => {
                // On a phone a finger is handled by the touch listeners.
                if (isMobile && e.pointerType === 'touch') return;
                e.currentTarget.setPointerCapture(e.pointerId);
                pipPosDraggingRef.current = true;
                pipTouchAnchorRef.current = null;
                if (e.pointerType !== 'mouse') {
                  const container = panelsContainerRef.current;
                  if (container) {
                    const c = container.getBoundingClientRect();
                    const at = pipScreenFor(pipPosRef.current);
                    pipTouchAnchorRef.current = { px: e.clientX - c.left, py: e.clientY - c.top, left: at.left, top: at.top };
                  }
                }
              }}
              onPointerMove={(e) => {
                if (!pipPosDraggingRef.current) return;
                if (isMobile && e.pointerType === 'touch') return;
                const container = panelsContainerRef.current;
                if (!container || !pairBox) return;
                const c = container.getBoundingClientRect();
                const a = pipTouchAnchorRef.current;
                const at = a
                  ? pipFromPoint(a.left + (e.clientX - c.left - a.px), a.top + (e.clientY - c.top - a.py))
                  : pipFromPoint(e.clientX - c.left, e.clientY - c.top);
                if (!at) return;
                const next = clampPip(at.x, at.y, pairBox.width, pairBox.height, pipDiameter);
                setPipPos(next);
                pipPosRef.current = next;
              }}
              onPointerUp={() => { pipPosDraggingRef.current = false; pipTouchAnchorRef.current = null; }}
              /* A touch the browser takes back ends the drag cleanly rather
                 than leaving it armed. */
              onPointerCancel={() => { pipPosDraggingRef.current = false; pipTouchAnchorRef.current = null; }}
              onPointerLeave={() => { pipPosDraggingRef.current = false; }}
            >
              {/* Phone: the bubble's own video takes no touches, so iOS cannot
                  claim the drag for the video element. */}
              <video ref={setPipVideoRef} autoPlay muted playsInline {...(isMobile ? PHONE_INLINE_EXTRAS : {})} className="w-full h-full object-cover" style={isMobile ? { transform: 'scaleX(-1)', pointerEvents: 'none' } : { transform: 'scaleX(-1)' }} />
            </div>
          )}
    </>
  );
  const errorBanner = (
    <>
        {/* Recording error banner */}
        {recordingError && (
          <div
            role="alert"
            className="flex items-start gap-2.5 px-4 py-2.5 text-sm"
            style={{ background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.3)', color: 'var(--editor-text)' }}
          >
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="flex-1">{recordingError}</span>
            <button
              type="button"
              onClick={() => setRecordingError(null)}
              className="flex-shrink-0 text-[var(--editor-text-muted)] hover:text-[var(--editor-text)] transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

    </>
  );

  /**
   * The recorder on a phone (docs/mobile-editor-layout-spec.md, section 2):
   * full screen, a slim top bar, the stage or stages at a size fixed from
   * the viewport when the recorder opened, and the tools pinned to the bottom.
   * Same panels, handlers and bubble as the desktop layout below.
   */
  if (isMobile) {
    /* Spec 15.1: one video in landscape runs the full visible height at its
       own aspect, centred; nothing beside it takes width from it. */
    const landscape = phoneV.landscape;
    /* Spec 15.20: side by side, each video at its maximum: full width in
       portrait, half the screen less the gutter in landscape, at its own
       aspect. One 44 row under each. */
    const bounds = recSingle ? { maxW: phoneV.w, maxH: phoneV.h }
      : recPair ? { maxW: recPair.maxW, maxH: recPair.maxH }
      : portraitBounds(phoneV, hasTwoSources);
    const pairSizes = hasTwoSources && secondarySource
      ? [
        fitAspect(panelAspects[`1:${primarySource.videoUrl}`] ?? DEFAULT_ASPECT, bounds.maxW, bounds.maxH),
        fitAspect(panelAspects[`2:${secondarySource.videoUrl}`] ?? DEFAULT_ASPECT, bounds.maxW, bounds.maxH),
      ]
      : null;
    const pairRows = REC.target;
    /* Spec 15.7: the close X at the top left. Landscape side by side, the
       left title starts 8 clear of it where the two would share a band. */
    const pairX = { left: phoneV.insets.left + REC.pad, top: phoneV.insets.top + REC.pad };
    const pairTitleIndent = (() => {
      if (!recPair || !pairSizes) return 0;
      const inner = phoneV.w - phoneV.insets.left - phoneV.insets.right;
      const left1 = phoneV.insets.left + (inner - (pairSizes[0].w + REC.gutter + pairSizes[1].w)) / 2;
      const block = Math.max(pairSizes[0].h, pairSizes[1].h) + REC.titleH + REC.titleGap + pairRows;
      const top0 = phoneV.insets.top + REC.pad;
      const title1Top = top0 + (recPair.rowsBottom - top0 - block) / 2 + (block - (pairSizes[0].h + REC.titleH + REC.titleGap + pairRows));
      const clearX = pairX.left + REC.target + REC.gap;
      const sharesBand = title1Top < pairX.top + REC.target && title1Top + REC.titleH > pairX.top;
      return sharesBand && left1 < clearX ? Math.ceil(clearX - left1) : 0;
    })();
    /* Portrait pair: stacked, each at the full width when the two fit. */
    const [bounds1, bounds2] = hasTwoSources && !landscape
      ? stackedPanelBounds(bounds, panelAspects[`1:${primarySource.videoUrl}`] ?? null, panelAspects[`2:${secondarySource?.videoUrl ?? ''}`] ?? null)
      : [bounds, bounds];
    const showVC1 = !primarySource.isCameraSource && !(primarySource.segment && primarySource.segment.startTime === primarySource.segment.endTime);
    const showVC2 = !!secondarySource && !secondarySource.isCameraSource && !(secondarySource.segment && secondarySource.segment.startTime === secondarySource.segment.endTime);
    const phoneTransport1 = (
      <PanelTransport
        phone
        compact={landscape}
        showVideoControls={showVC1}
        sideBySide={false}
        /* Spec 15.9: scrubbing holds the landscape pill on screen. */
        onScrubStart={() => { setTimelineScrubbing(true); handleScrubStart1(); }}
        onScrubEnd={() => { setTimelineScrubbing(false); handleScrubEnd1(); }}
        isPlaying={p1IsPlaying} onTogglePlay={handleTogglePlay1}
        loading={p1Ui === 'loading'}
        segStart={primarySource.segment?.startTime ?? 0}
        segEnd={segmentEndOf(primarySource.segment?.endTime ?? null, p1Duration)}
        currentTime={p1CurrentTime} onSeek={handleSeek1}
        speed={p1Speed} onSpeedChange={handleSpeedChange1}
        isMuted={p1Muted} onToggleMute={handleToggleMute1}
      />
    );
    const panelCommon = {
      sideBySide: hasTwoSources,
      phoneBounds: bounds,
      phoneRows: hasTwoSources,
      phoneMuteLocked: hasTwoSources && p1IsPlaying && p2IsPlaying,
      phoneRowInset: 0,
      drawingTool,
      drawColor,
      onSetDrawingTool: setDrawingTool,
      onSetDrawColor: setDrawColor,
    };
    /* Spec 10.4: one control that records, then saves the take and stays
       open for the next; back is the only way out, and asks during a take. */
    /* Spec 15.27: every recorder control goes through one table
       (recorderTaps.ts, unit tested), so Save, the trash and the alert do
       exactly what it says. */
    const tap = (control: Parameters<typeof recorderTap>[0]) => {
      const t = recorderTap(control, isRecording);
      switch (t.kind) {
        case 'start': void handleStartRecording(); break;
        case 'stop': handleStopRecording(t.mode); break;
        case 'ask': setPhoneAlert(t.alert); break;
        case 'abort': setPhoneAlert(null); handleAbortRecording(); break;
        case 'dismiss': setPhoneAlert(null); break;
        case 'none': break;
      }
    };
    const handleBackPress = () => { if (isRecording) setPhoneAlert('back'); else handleClose(); };
    const recordSave = (size: number, showLabel = true) => (
      <RecordSaveButton recording={isRecording} size={size} showLabel={showLabel} onRecord={() => tap('record-save')} onSave={() => tap('record-save')} />
    );
    /* Spec 15.28: in the bottom-bar layouts the trash sits directly to the
       right of Record/Save, 8 away, only while recording; Record/Save never
       moves when a take starts. */
    const recordSaveWithTrash = () => (
      <div className="relative flex-none" style={{ width: REC.record, height: REC.record }}>
        {recordSave(REC.record, false)}
        {isRecording && (
          <div className="absolute top-1/2 -translate-y-1/2" style={{ left: REC.record + REC.gap }} data-testid="trash-beside-save">
            {tools.trash(REC.target)}
          </div>
        )}
      </div>
    );
    const phoneAlerts = (
      <>
        {phoneAlert === 'back' && (
          <RecorderAlert
            title="Save this clip?"
            actions={[
              { label: 'Save', role: 'primary', onPress: () => { setPhoneAlert(null); handleStopRecording('close'); } },
              { label: 'Discard', role: 'destructive', onPress: () => { setPhoneAlert(null); handleClose(); } },
              { label: 'Keep Recording', role: 'cancel', onPress: () => setPhoneAlert(null) },
            ]}
          />
        )}
        {phoneAlert === 'discard' && (
          <RecorderAlert
            title="Discard this clip?"
            actions={[
              { label: 'Discard', role: 'destructive', onPress: () => tap('alert-discard') },
              { label: 'Keep Recording', role: 'cancel', onPress: () => tap('alert-keep') },
            ]}
          />
        )}
      </>
    );
    /* Spec 15: the recorder's controls, one recipe on every screen: 44
       circles (36 in a squeezed column), no fill of their own except an
       active tool's biscayne, icons 24 (20 at 36). */
    const canAnnotateNow = hasTwoSources ? sharedCanAnnotate : true;
    const markerHex = DRAW_COLORS.find((c) => c.value === drawColor)?.hex ?? COLOR_HEX.red;
    const icon = (size: number) => (size >= 44 ? 'w-6 h-6' : 'w-5 h-5');
    const tools = {
      /* Spec 13.1: one tap steps the marker through the colours, then off. */
      marker: (size: number) => (
        <RecButton key="marker" size={size} testId="pen-btn" disabled={!canAnnotateNow}
          active={drawingTool === 'brush'} activeColor={markerHex} pressed={drawingTool === 'brush'}
          label={drawingTool === 'brush' ? `Marker, ${DRAW_COLORS.find((c) => c.value === drawColor)?.label ?? ''}, tap for the next colour` : 'Marker, off, tap to draw'}
          onClick={() => {
            const next = nextMarker(drawingTool, drawColor, MARKER_PALETTE);
            setDrawColor(next.color);
            setDrawingTool(next.tool);
          }}>
          <svg className={icon(size)} fill="currentColor" viewBox="0 0 24 24" style={drawingTool === 'brush' ? { filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.6))' } : undefined} aria-hidden="true"><path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3zm13.71-9.37-1.34-1.34a1 1 0 0 0-1.41 0L9 12.25 11.75 15l8.96-8.96a1 1 0 0 0 0-1.41z" /></svg>
        </RecButton>
      ),
      eraser: (size: number) => (
        <RecButton key="eraser" size={size} label="Erase drawing" disabled={!canAnnotateNow}
          active={drawingTool === 'eraser'} pressed={drawingTool === 'eraser'}
          onClick={() => setDrawingTool((t) => (t === 'eraser' ? 'none' : 'eraser'))}>
          <svg className={icon(size)} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M15.14 3c-.51 0-1.02.2-1.41.59L2.59 14.73c-.78.77-.78 2.04 0 2.83L5.03 20H20v-2h-8.56l7.97-7.97c.78-.78.78-2.05 0-2.83l-2.85-2.85A2 2 0 0015.14 3z" /></svg>
        </RecButton>
      ),
      undo: (size: number) => (
        <RecButton key="undo" size={size} label="Undo" disabled={historyDepth.undo === 0} onClick={handleSharedUndo}>
          <svg className={icon(size)} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" /></svg>
        </RecButton>
      ),
      redo: (size: number) => (
        <RecButton key="redo" size={size} label="Redo" disabled={historyDepth.redo === 0} onClick={handleSharedRedo}>
          <svg className={icon(size)} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.06-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" /></svg>
        </RecButton>
      ),
      speed: (size: number, panel: 1 | 2 = 1, which?: string) => {
        const sp = panel === 1 ? p1Speed : p2Speed;
        const set = panel === 1 ? handleSpeedChange1 : handleSpeedChange2;
        return <SpeedButton key={`speed${panel}`} size={size} speed={sp} which={which} disabled={panel === 1 ? !showVC1 : !showVC2} onCycle={() => set(SPEEDS[(SPEEDS.indexOf(sp) + 1) % SPEEDS.length])} />;
      },
      mute: (size: number, panel: 1 | 2 = 1, which?: string) => {
        const muted = panel === 1 ? p1Muted : p2Muted;
        return (
          <RecButton key={`mute${panel}`} size={size} label={`${which ? `${which} video, ` : ''}${muted ? 'Unmute' : 'Mute'}`} pressed={muted}
            disabled={panel === 1 ? !showVC1 : !showVC2}
            color={muted ? 'var(--editor-text-muted)' : 'var(--editor-text)'}
            onClick={panel === 1 ? handleToggleMute1 : handleToggleMute2}>
            <MuteGlyph muted={muted} className={icon(size)} />
          </RecButton>
        );
      },
      camera: (size: number) => (
        <CameraToggle key="camera" size={size} on={pipEnabled} disabled={primarySource.isCameraSource}
          onToggle={() => {
            const on = !pipEnabled;
            savePipOn(on);
            if (on) { setBubble(BUBBLE_HOME); saveBubble(BUBBLE_HOME); }
            setPipEnabled(on);
          }} />
      ),
      trash: (size: number) => <TrashButton key="trash" size={size} onClick={() => tap('trash')} />,
      compare: (size: number) => (onAddSecondarySource
        ? (
          <RecButton key="compare" size={size} label={hasTwoSources ? 'Change comparison video' : 'Add comparison video'} onClick={onAddSecondarySource}>
            <svg className={icon(size)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
              <rect x="2" y="4" width="9" height="16" rx="1" />
              <rect x="13" y="4" width="9" height="16" rx="1" />
              <path strokeLinecap="round" d="M11.5 12h1" />
            </svg>
          </RecButton>
        )
        : null),
    };
    const phonePanels = (
        <div
          ref={panelsContainerRef}
          /* Spec 15.10: landscape, the pair is bottom-aligned, so a shorter
             video leaves its space at the top and the two rows share a line. */
          /* Portrait (15.4): stacked, 24 from one video's row to the next title. */
          className={`relative flex-none flex ${hasTwoSources ? (landscape ? 'flex-row items-end justify-center' : 'flex-col items-center') : 'justify-center'}`}
          style={hasTwoSources ? { gap: landscape ? REC.gutter : REC.stackSpace } : undefined}
        >
          {/* Recording (spec 9.4): a 2 red inner outline around the stage,
              drawn above the pictures; the header carries the pulsing dot.
              Landscape, one video: the clock is over the top centre of the
              video (spec 15.1). On screen only. */}
          {isRecording && (
            <div className="absolute inset-0 pointer-events-none z-[22]" style={{ boxShadow: 'inset 0 0 0 2px rgb(239, 68, 68)' }} aria-hidden="true" />
          )}
          <VideoPanel
            {...panelCommon}
            source={primarySource}
            phoneBounds={bounds1}
            phoneStageId={1}
            onPhoneTap={recSingle ? toggleTimeline : undefined}
            onPhoneScrub={hasTwoSources ? phoneScrub1 : undefined}
            phoneTitleIndent={recPair ? pairTitleIndent : 0}
            onPhoneAspect={(a) => notePanelAspect(`1:${primarySource.videoUrl}`, a)}
            zoomStateRef={panel1ZoomRef}
            videoRef={video1Ref}
            onVideoElement={onVideo1Element}
            onHistoryReset={dropPanel1History}
            pipOverlay={bubblePanel === 1 ? phoneBubble : undefined}
            annotationCanvasRef={annotationCanvas1Ref}
            isPlaying={p1IsPlaying}
            isLoading={p1Ui === 'loading'}
            currentTime={p1CurrentTime}
            duration={p1Duration}
            speed={p1Speed}
            isMuted={p1Muted}
            onTogglePlay={handleTogglePlay1}
            onSeek={handleSeek1}
            onScrubStart={handleScrubStart1}
            onScrubEnd={handleScrubEnd1}
            onSpeedChange={handleSpeedChange1}
            onToggleMute={handleToggleMute1}
            ref={panel1Ref}
            onStrokeCommitted={() => noteStroke(1)}
          />
          {hasTwoSources && secondarySource && (
            <VideoPanel
              {...panelCommon}
              source={secondarySource}
              phoneBounds={bounds2}
              phoneStageId={2}
              onPhoneScrub={phoneScrub2}
              onPhoneAspect={(a) => notePanelAspect(`2:${secondarySource.videoUrl}`, a)}
              zoomStateRef={panel2ZoomRef}
              videoRef={video2Ref}
              onVideoElement={onVideo2Element}
              onHistoryReset={dropPanel2History}
              pipOverlay={bubblePanel === 2 ? phoneBubble : undefined}
              annotationCanvasRef={annotationCanvas2Ref}
              isPlaying={p2IsPlaying}
              isLoading={p2Ui === 'loading'}
              currentTime={p2CurrentTime}
              duration={p2Duration}
              speed={p2Speed}
              isMuted={p2Muted}
              onTogglePlay={handleTogglePlay2}
              onSeek={handleSeek2}
              onScrubStart={handleScrubStart2}
              onScrubEnd={handleScrubEnd2}
              onSpeedChange={handleSpeedChange2}
              onToggleMute={handleToggleMute2}
              ref={panel2Ref}
              onStrokeCommitted={() => noteStroke(2)}
            />
          )}
          <SavedToast shownAt={savedToastAt} onDone={clearSavedToast} />
        </div>
    );
    /* Spec 13.9: one tree for both orientations. Rotation changes classes
       and styles only, so the stage (both videos, the drawing canvases, the
       bubble) and everything recording from it is never remounted. Two
       trees used to put the stage under a different parent in each, and a
       turn of the phone mounted new, empty video elements mid-take.

       Landscape (spec 10.5): no top bar; tools down the left rail; the stage
       with its thin timeline; back, record and save, the trash or the
       comparison, and the camera down the right rail. Portrait (10.6): the
       top bar, the stage, tools and timeline under it, open space, then the
       record control in the thumb zone. */
    const phoneInner = (
      /* pan-y (spec 14.11): a vertical swipe from the column or the space
         beside the video scrolls the page, which hides Safari's bars. The
         video's drawing surface stays touch-action none, so it still draws. */
      <div className={`relative ${landscape ? '' : 'flex flex-col'} h-full w-full overflow-hidden`} style={{ background: landscape ? '#000' : 'var(--editor-bg)', touchAction: landscape ? 'pan-y' : undefined }}>
        {phoneAlerts}
        {/* Portrait header, 44 (spec 15.3, 15.7): back to the lesson (the
            "Lesson" pill: a header, not the video), the clip's name (Subhead
            15/20 semibold, at most 120), and during a take the clock. The
            comparison and the trash are in the tools row. */}
        {/* The clip's name takes the header's free width (no 120 cap now): two lines,
            then a measured step down; a header that still needs more grows, never cuts. */}
        <div className="items-center gap-2 flex-none py-1" style={{ display: landscape ? 'none' : 'flex', minHeight: PHONE_HEADER, paddingLeft: REC.pad, paddingRight: REC.pad, background: 'var(--editor-header)', borderBottom: '1px solid var(--editor-border)' }}>
          {!landscape && (
            <>
              <BackButton flush variant="inverse" label="Lesson" onClick={handleBackPress} />
              <FitText lines={2} className="flex-1 font-semibold text-[15px] leading-[20px] text-[var(--editor-text)]">
                {primarySource.label}
              </FitText>
              {isRecording && <TakeClock seconds={recordingElapsed} />}
            </>
          )}
        </div>
        {/* Landscape, one video (spec 15.1, 15.7, 15.8, 15.12 to 15.14): the
            close X alone at the top left; the tools in one column in the left
            margin, or split into a left column (marker, eraser, undo, redo)
            and a right one (speed, mute, camera, and compare when not
            recording). Each pill is 44 buttons with 6 round them, a chevron
            at its top. Record/Save sits 8 under the left pill and, during a
            take, the trash 8 under that. Either chevron collapses both
            columns to the chevron (Record/Save and the trash stay). Never two
            buttons side by side in a column; nothing here is recorded. */}
        {recSingle && (
          <>
            <CloseX onClick={handleBackPress} style={{ position: 'absolute', left: recSingle.x.left, top: recSingle.x.top, zIndex: 36 }} />
            {/* Spec 15.27: anchored from the bottom and capped, so Record/Save
                and the trash stay on screen whatever the visible height does
                after the layout was read; the pill gives way (its tools
                scroll). */}
            <div
              data-testid="recorder-column"
              className="absolute z-[35] flex flex-col items-center justify-end"
              style={{ left: recSingle.left.left, bottom: recSingle.leftBottom, width: recSingle.left.w, gap: REC.gap, maxHeight: `min(${recSingle.leftMaxH}px, calc(100% - ${recSingle.x.top + recSingle.x.h + REC.gap}px - ${recSingle.leftBottom}px))` }}
            >
              <div
                className="lve-glass rounded-full flex flex-col items-center w-full overflow-hidden min-h-0"
                style={{ padding: recSingle.pad, gap: recSingle.gap, maxHeight: recSingle.leftPillMax ?? undefined, flex: '0 1 auto' }}
              >
                <ColumnChevron collapsed={columnsCollapsed} onToggle={toggleColumns} width={recSingle.size} />
                {!columnsCollapsed && (
                  /* Squeeze only (926 x 300): the tools scroll inside the pill
                     so Record/Save and the trash stay on screen. */
                  <div className="flex flex-col items-center min-h-0 overflow-y-auto" style={{ gap: recSingle.gap }}>
                    {tools.marker(recSingle.size)}
                    {tools.eraser(recSingle.size)}
                    {tools.undo(recSingle.size)}
                    {tools.redo(recSingle.size)}
                    {!recSingle.split && (
                      <>
                        {tools.speed(recSingle.size)}
                        {tools.mute(recSingle.size)}
                        {tools.camera(recSingle.size)}
                        {!isRecording && tools.compare(recSingle.size)}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-none flex justify-center" style={{ width: REC.record }}>{recordSave(REC.record, false)}</div>
              {isRecording && (
                <div className="flex-none lve-glass rounded-full">{tools.trash(recSingle.size)}</div>
              )}
            </div>
            {recSingle.right && (
              <div
                data-testid="recorder-right-column"
                className="absolute z-[35] lve-glass rounded-full flex flex-col items-center"
                style={{ left: recSingle.right.left, top: recSingle.right.top, width: recSingle.right.w, padding: recSingle.pad, gap: recSingle.gap }}
              >
                <ColumnChevron collapsed={columnsCollapsed} onToggle={toggleColumns} width={recSingle.size} />
                {!columnsCollapsed && (
                  <>
                    {tools.speed(recSingle.size)}
                    {tools.mute(recSingle.size)}
                    {tools.camera(recSingle.size)}
                    {!isRecording && tools.compare(recSingle.size)}
                  </>
                )}
              </div>
            )}
          </>
        )}
        {/* The stage column, the same element in both orientations. */}
        <div
          className={landscape ? 'absolute inset-0 flex flex-col items-center justify-center' : 'flex-1 min-h-0 flex flex-col justify-center'}
          /* Spec 15.1: one video in landscape is centred on the whole screen,
             under the columns. */
          style={recPair
            /* Spec 15.29: the pair centred in the space above the bottom bar, between the safe areas. */
            ? { paddingTop: phoneV.insets.top + REC.pad, paddingBottom: phoneV.h - recPair.rowsBottom, paddingLeft: phoneV.insets.left, paddingRight: phoneV.insets.right }
            : undefined}
        >
          {/* Portrait (15.29): the stack (titles, videos, rows, the timeline
              row) scrolls inside this area when it is taller than the space
              above the tools row, and is centred when it is not; the tools
              row and Record/Save stay pinned below. Landscape keeps the
              centred stage. */}
          <div className={landscape ? 'contents' : 'flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col'} style={landscape ? undefined : { touchAction: 'pan-y' }} data-testid={landscape ? undefined : 'recorder-stack'}>
            <div className={landscape ? 'contents' : 'my-auto flex flex-col'}>
              {errorBanner}
              <EditorDebugOverlay corner="bottom" videos={[{ label: 'panel 1', ref: video1Ref }, { label: 'panel 2', ref: video2Ref }]} />
              {phonePanels}
              {/* Portrait (15.3): the timeline row directly under the video,
                  as wide as it. Landscape: the floating pill below (15.1). */}
              <div className="flex-none flex flex-col" style={landscape ? { display: 'none' } : { paddingLeft: REC.portraitSide, paddingRight: REC.portraitSide }}>
                {!hasTwoSources && !landscape && phoneTransport1}
              </div>
            </div>
          </div>
          {!landscape && (() => {
            /* Spec 15.3: marker, eraser, undo, redo at the left; speed, mute,
               the camera and the comparison (the trash in a take) at the
               right. Side by side (15.4) speed and mute are per video. The
               gap is 8 where it fits, down to 6 (7.4 for eight at 428). */
            const left = [tools.marker(REC.target), tools.eraser(REC.target), tools.undo(REC.target), tools.redo(REC.target)];
            const right = [
              ...(hasTwoSources ? [] : [tools.speed(REC.target), tools.mute(REC.target)]),
              tools.camera(REC.target),
              ...(isRecording ? [] : [tools.compare(REC.target)]),
            ].filter(Boolean);
            const gap = rowGap(phoneV.w - phoneV.insets.left - phoneV.insets.right - 2 * REC.pad, left.length + right.length);
            return (
              <div data-testid="recorder-tools-row" className="flex-none flex items-center justify-between" style={{ height: REC.target, paddingLeft: REC.pad, paddingRight: REC.pad }}>
                <div className="flex items-center" style={{ gap }}>{left}</div>
                <div className="flex items-center" style={{ gap }}>{right}</div>
              </div>
            );
          })()}
        </div>
        {/* Landscape, one video (spec 15.1, 15.9, 15.16): the timeline pill,
            40 tall, 8 in from the video's sides and 8 above its bottom edge
            or the bottom safe area. Play, scrubber, time, nothing else. It
            fades out 3 s into playback or a take; a single tap on the video
            shows or hides it and never draws. */}
        {recSingle && (
          <>
            <div
              data-testid="recorder-timeline"
              className="absolute z-[35] lve-glass rounded-full flex items-center px-1"
              style={{
                left: recSingle.pill.left,
                top: recSingle.pill.top,
                width: recSingle.pill.w,
                height: recSingle.pill.h,
                opacity: timelineShown ? 1 : 0,
                transition: 'opacity 200ms ease',
                pointerEvents: timelineShown ? 'auto' : 'none',
              }}
              aria-hidden={timelineShown ? undefined : true}
            >
              {phoneTransport1}
            </div>
            {isRecording && (
              <div className="absolute left-1/2 -translate-x-1/2 z-[36] pointer-events-none" style={{ top: recSingle.timerTop }}>
                <TakeClock seconds={recordingElapsed} glass />
              </div>
            )}
          </>
        )}
        {/* Portrait (15.3): Record/Save pinned at the bottom, centred, 12
            above and below. None of it moves when a take starts. */}
        <div className="flex-none grid items-center" style={{ display: landscape ? 'none' : 'grid', height: RECORD_ZONE_H, paddingLeft: REC.pad, paddingRight: REC.pad, gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', columnGap: REC.gap }}>
          {!landscape && (
            <>
              <div />
              {recordSaveWithTrash()}
              <div />
            </>
          )}
        </div>
        {/* Landscape, side by side (spec 15.2): the close X at the top left;
            one glass bar along the bottom inside the safe areas: marker,
            eraser, undo and redo at the left, Record/Save in the exact
            centre, the camera and the comparison (or the trash) at the far
            right. When the rows cannot hold speed and mute (15.10), each
            video's pair sits on its own side of the bar. The take's clock
            is just right of Record/Save. */}
        {recPair && (
          <>
            <CloseX onClick={handleBackPress} style={{ position: 'absolute', left: pairX.left, top: pairX.top, zIndex: 36 }} />
            <div
              data-testid="recorder-bottom-bar"
              className="absolute z-[35] lve-glass rounded-full grid items-center"
              style={{ left: recPair.bar.left, top: recPair.bar.top, width: recPair.bar.w, height: recPair.bar.h, padding: REC.pillPad, gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', columnGap: REC.gap }}
            >
              <div className="flex items-center min-w-0" style={{ gap: REC.gap }}>
                {tools.marker(REC.target)}
                {tools.eraser(REC.target)}
                {tools.undo(REC.target)}
                {tools.redo(REC.target)}
              </div>
              {recordSaveWithTrash()}
              {/* Spec 15.28: the trash beside Record/Save takes the first 52 of
                  this cell; the clock sits after it. */}
              <div className="flex items-center justify-between min-w-0" style={{ gap: REC.gap, paddingLeft: isRecording ? REC.target + REC.gap : 0 }}>
                <div className="flex-none">{isRecording && <TakeClock seconds={recordingElapsed} />}</div>
                <div className="flex items-center" style={{ gap: REC.gap }}>
                  {tools.camera(REC.target)}
                  {!isRecording && tools.compare(REC.target)}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
    if (embedded) return phoneInner;
    return (
      <div
        className="fixed inset-0 z-[70]"
        style={{
          background: 'var(--editor-bg)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {phoneInner}
      </div>
    );
  }

  const inner = (
    <div className={embedded
      ? 'flex flex-col h-full w-full overflow-hidden rounded-xl border border-[var(--editor-border)] shadow-2xl'
      : 'w-full h-full md:w-auto md:h-auto rounded-none md:rounded-xl md:max-w-5xl mx-0 md:mx-auto flex flex-col md:max-h-[95vh] overflow-hidden shadow-2xl border border-[var(--editor-border)]'
    } style={{ background: embedded ? 'var(--editor-sidebar)' : 'var(--editor-bg)' }}>

        {/* ── Header ── */}
        {/* py-2.5, not py-3: the recipe's text buttons are 36 where these were
            32, so trimming 2 of padding a side keeps this header at the 56 it
            was. Header height comes straight out of the video. */}
        {/* On a phone this row is laid out by order: Done, then Start
            Recording at the right; while recording, the clock and Discard on
            the first line and the two saves sharing the second. */}
        <div className="flex items-center gap-3 px-4 py-2.5 phone:px-3 phone:gap-2 flex-shrink-0 flex-wrap" style={{ background: 'var(--editor-header)', borderBottom: '1px solid var(--editor-border)' }}>
          {/* Labelled, and the label changes with what the control will do.
              Idle it is a safe close, so "Done". Mid-recording handleClose sets
              canceledRef, which makes recorder.onstop drop the blob before the
              thumbnail step, so the take is thrown away: "Discard". */}
          {/* Hidden while recording. It used to read "Discard" there and do
              exactly what Abort does, so the take had two ways out with two
              names. Recording now has three exits and one meaning each: Save &
              Continue, Save & Close, and Discard. */}
          {!isRecording && (
          <button
            type="button"
            onClick={handleClose}
            className={EBTN_GHOST}
            title="Close the clip recorder"
            aria-label="Done, close the clip recorder"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="hidden md:inline text-sm">Done</span>
          </button>
          )}
          {/* The segment's name is not repeated here. Each panel's own label
              row carries it, which is the only place it shows in full and the
              only place that works when two panels have different names. */}
          {/* Hidden on a phone, where it pushed Start Recording onto a line of
              its own. The red button says what this screen is for. */}
          <span className="font-semibold text-[var(--editor-text)] flex-shrink-0 phone:hidden">Clip Recorder</span>
          <div className="flex-1 phone:order-1" />
          {/* Add side-by-side video — shown only when opened directly via "Open in Editor" shortcut */}
          {/* The same slot does both jobs. It sat empty once a comparison was
              set, which left no way to swap one; the label changes with what
              the press will do. */}
          {onAddSecondarySource && !isRecording && (
            <button
              type="button"
              onClick={onAddSecondarySource}
              className={`${EBTN_ICON} phone:order-2`}
              title={hasTwoSources ? 'Change comparison video' : 'Add comparison video'}
              aria-label={hasTwoSources ? 'Change comparison video' : 'Add comparison video'}
            >
              {/* Two-panel split icon */}
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <rect x="2" y="4" width="9" height="16" rx="1" />
                <rect x="13" y="4" width="9" height="16" rx="1" />
                <path strokeLinecap="round" d="M11.5 12h1" />
              </svg>
            </button>
          )}
          {/* Camera overlay checkbox — only when primary source is NOT camera (camera-as-primary already has the camera) */}
          {!primarySource.isCameraSource && (
            <label className="hidden md:flex items-center gap-2 cursor-pointer text-sm text-[var(--editor-text)] flex-shrink-0">
              <div
                className={`relative w-9 h-5 rounded-full transition-colors ${pipEnabled ? 'bg-[var(--color-db-oasis)]' : 'bg-[var(--editor-border)]'}`}
                onClick={(e) => { e.preventDefault(); setPipEnabled(prev => !prev); }}
              >
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${pipEnabled ? 'translate-x-4' : ''}`} />
              </div>
              Camera overlay
            </label>
          )}
          {isRecording && (
            /* On-screen only. Nothing here is drawn to the composite canvas.
               The word is no longer hidden when embedded: embedded is how the
               clip editor is always opened from the lesson editor, so hiding
               it there left an 8px dot as the entire recording indicator. */
            <div
              className="flex items-center gap-2 flex-shrink-0 rounded-full px-2.5 py-1"
              style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)' }}
              title="Recording"
              aria-live="polite"
            >
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-semibold text-red-400">REC</span>
              <span className="text-sm font-semibold text-red-400 tabular-nums">
                {formatTime(recordingElapsed)}
              </span>
            </div>
          )}
          {/* Action buttons */}
          {!isRecording ? (
            <button
              type="button"
              data-testid="start-recording-btn"
              onClick={handleStartRecording}
              className={`${EBTN_RECORD} phone:order-2`}
            >
              <div className="w-2 h-2 rounded-full bg-white" />
              Start Recording
            </button>
          ) : (
            <div className="flex items-center gap-1.5 flex-shrink-0 phone:contents">
              {/* Filled, then washed, then red: the two that save carry weight
                  in that order and Abort carries none, so which two save is
                  readable without reading the labels. */}
              <button
                type="button"
                onClick={() => handleStopRecording('continue')}
                className={`${EBTN_PRIMARY} phone:order-4 phone:flex-1`}
                title="Save this recording and record another"
              >
                Save &amp; Continue
              </button>
              <button
                type="button"
                data-testid="save-close-recording-btn"
                onClick={() => handleStopRecording('close')}
                className={`${EBTN_SECONDARY} phone:order-4 phone:flex-1`}
                title="Save this recording and close the recorder"
              >
                Save &amp; Close
              </button>
              {/* Separates the two that save from the one that discards. */}
              <div className="w-px h-5 mx-1.5 flex-shrink-0 phone:hidden" style={{ background: 'var(--editor-border)' }} />
              {/* Phone line break between the clock and the two saves. */}
              <div className="hidden phone:block phone:order-3 basis-full h-0" aria-hidden="true" />
              <button
                type="button"
                onClick={handleAbortRecording}
                className={`${EBTN_DESTRUCTIVE} phone:order-2`}
                title="Discard this recording and stay in the recorder"
              >
                Discard
              </button>
            </div>
          )}
        </div>

        {errorBanner}
        <EditorDebugOverlay corner="bottom" videos={[{ label: 'panel 1', ref: video1Ref }, { label: 'panel 2', ref: video2Ref }]} />

        {/* ── Video panels ── */}
        <div
          ref={panelsContainerRef}
          /* pt-2 when paired: 16 of top padding sat under a header that already
             has its own bottom border, which read as a dead band above the
             titles. Single source keeps p-4 exactly. */
          /* Phone: tighter padding, and a pair stacks in portrait rather than
             splitting 375 into two 150px pictures. */
          className={`relative flex overflow-hidden flex-1 min-h-0 ${hasTwoSources ? 'px-4 pt-4 pb-4 phone:flex-col phone:gap-2' : 'p-4 gap-4 justify-center'} phone:p-2`}
          /* On-screen only: this is the container the panels sit in, not the
             composite canvas, so none of it reaches the recorded file.
             Deliberately NOT pulsing. The dot in the header pulses, which is
             enough to read as live; a whole border breathing in the coach's
             peripheral vision for a three-minute take is a distraction, and it
             would ignore prefers-reduced-motion. Static red states the fact
             and then stays out of the way. */
          style={isRecording ? {
            boxShadow: 'inset 0 0 0 2px rgb(239, 68, 68), inset 0 0 24px rgba(239, 68, 68, 0.25)',
          } : undefined}
        >
          {/* Each panel is a surface one step above the recorder's background,
              so the pair reads as two panels rather than as one dark field.
              --editor-timeline sits between --editor-sidebar (the embedded
              recorder's own ground) and --editor-surface. */}
          <div
            className="flex-1 min-w-0 min-h-0 flex flex-col p-2 rounded-lg"
            style={hasTwoSources ? { background: 'var(--editor-timeline)' } : undefined}
          >
            <VideoPanel
              source={primarySource}
              videoRef={video1Ref}
              annotationCanvasRef={annotationCanvas1Ref}
              isPlaying={p1IsPlaying}
              currentTime={p1CurrentTime}
              duration={p1Duration}
              speed={p1Speed}
              isMuted={p1Muted}
              onTogglePlay={handleTogglePlay1}
              onSeek={handleSeek1}
              onScrubStart={handleScrubStart1}
              onScrubEnd={handleScrubEnd1}
              onSpeedChange={handleSpeedChange1}
              onToggleMute={handleToggleMute1}
              sideBySide={hasTwoSources}
              onVideoBoxHeight={setDividerHeight}
              ref={panel1Ref}
              drawingTool={drawingTool}
              drawColor={drawColor}
              onSetDrawingTool={setDrawingTool}
              onSetDrawColor={setDrawColor}
              onStrokeCommitted={() => noteStroke(1)}
            />
          </div>

          {/* One rule instead of a gutter. Its height is the video area's, not
              the column's, so it does not run past the pictures into the
              titles or the transports. */}
          {hasTwoSources && secondarySource && (
            /* No padding at all: the panels meet flush and the rule is the only
               thing between them. This gives 16px of width back to the pair
               rather than costing any. */
            <div className="flex-shrink-0 self-stretch flex flex-col justify-center phone:hidden" aria-hidden="true">
              {/* --editor-selected-text, not biscayne. #1A6B8A against a raised
                  panel is barely a step; #7EB4D4 is the lightest value in the
                  editor set and is the only one that reads as a rule at 1px. */}
              <div className="w-px" style={{ background: 'var(--editor-selected-text)', height: dividerHeight }} />
            </div>
          )}
          {hasTwoSources && secondarySource && (
            <div
              className="flex-1 min-w-0 min-h-0 flex flex-col p-2 rounded-lg"
              style={{ background: 'var(--editor-timeline)' }}
            >
              <VideoPanel
                source={secondarySource}
                videoRef={video2Ref}
                annotationCanvasRef={annotationCanvas2Ref}
                isPlaying={p2IsPlaying}
                currentTime={p2CurrentTime}
                duration={p2Duration}
                speed={p2Speed}
                isMuted={p2Muted}
                onTogglePlay={handleTogglePlay2}
                onSeek={handleSeek2}
                onScrubStart={handleScrubStart2}
                onScrubEnd={handleScrubEnd2}
                onSpeedChange={handleSpeedChange2}
                onToggleMute={handleToggleMute2}
                sideBySide
                ref={panel2Ref}
                drawingTool={drawingTool}
                drawColor={drawColor}
                onSetDrawingTool={setDrawingTool}
                onSetDrawColor={setDrawColor}
                onStrokeCommitted={() => noteStroke(2)}
              />
            </div>
          )}

          {pipBubble}
        </div>

        {/* One set of drawing tools for both panels, full width beneath them.
            The pen, the colour and the history act on whichever canvas the
            coach draws on — nothing about stroke routing changes. */}
        {hasTwoSources && secondarySource && (
          <div className="px-4 flex-shrink-0 flex justify-center">
            <DrawToolbar
              fullWidth
              drawingTool={drawingTool}
              setDrawingTool={setDrawingTool}
              drawColor={drawColor}
              setDrawColor={setDrawColor}
              canAnnotate={sharedCanAnnotate}
              undoDepth={historyDepth.undo}
              redoDepth={historyDepth.redo}
              onUndo={handleSharedUndo}
              onRedo={handleSharedRedo}
            />

          </div>
        )}

      </div>
  );

  if (embedded) return inner;
  return (
    <div className="fixed inset-0 md:inset-8 z-[70] flex items-center justify-center bg-black/70 rounded-none md:rounded-xl overflow-hidden">
      <div className="w-full h-full md:w-auto md:h-auto flex items-center justify-center"
        style={{
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {inner}
      </div>
    </div>
  );
}
